import { useState, useRef, useEffect, useMemo } from 'react';
import api from '../services/api';
import { useZohoSession } from '../contexts/ZohoSessionContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Upload, Plus, FileText, Download, X, Search, Trash2, Package, ChevronDown, Clock, Copy, ExternalLink, Building2, Loader2, Edit2, Eye, Zap, RefreshCw, CheckCircle2, Settings2, Mail, PoundSterling, Save, UserRound, ChevronLeft } from 'lucide-react';
import { toast } from 'sonner';
import { AddToCrmModal } from '../components/AddToCrmModal';
import LinkCrmAccountModal from '../components/LinkCrmAccountModal';
import EditCrmQuoteModal from '../components/EditCrmQuoteModal';
import ParsedOrderViewModal from '../components/ParsedOrderViewModal';
import { QuoteStagePill } from '../components/QuoteStagePill';
import CrmRevisePromptModal from '../components/CrmRevisePromptModal';
import { useUIPreview } from '../contexts/UIPreviewContext';
import { useActivityBar } from '../contexts/ActivityBarContext';
import { modalV2OverlayClass } from '../utils/modalUiV2';
import { useV3Window } from '../v3/useV3Window';
import QuoteLineEditor from '../components/QuoteLineEditor';
import { useNavLinkStyles, DEFAULT_LINK_COLORS } from '../utils/navLinkStyles';
import { ICON_MAP } from '../utils/navHeaderRegistry';
import { getPageZoom } from '../utils/modalViewport';
import QuotePriceListsPanel from '../components/QuotePriceListsPanel';
import ProductGroupModal from '../components/ProductGroupModal';

const API = process.env.REACT_APP_BACKEND_URL;

// Formats a YYYY-MM-DD price date as "12 Aug 26" (shown in small text under a line's Unit Price).
const fmtPriceDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(`${iso}T00:00:00`).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' });
  } catch (_) { return iso; }
};

// Helper function to format currency
const formatCurrency = (value) => {
  const num = parseFloat(value) || 0;
  return num.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

// "LB - £12.50 12 Aug 26": the customer's Last Bought price and date, shown in small text under a line's Unit Price.
const fmtLastBought = (price, iso) => `LB - £${formatCurrency(price)} ${fmtPriceDate(iso)}`;

// Every typable box on the quote: a light orange tint with an inset edge so it is obvious where you can type.
// It goes white with an orange outline while you are typing in it.
const QUOTE_FIELD_CLS = 'rounded-xl bg-orange-50 border-orange-200 shadow-inner focus-visible:bg-white focus-visible:border-orange-400 focus-visible:ring-2 focus-visible:ring-orange-200';
const QUOTE_SELECT_CLS = 'rounded-xl bg-orange-50 border-orange-200 shadow-inner focus:bg-white focus:border-orange-400 focus:ring-2 focus:ring-orange-200';

// The empty line that is always waiting at the bottom of the quote. "Blank" = no code, no description and no price.
const newBlankQuoteLine = () => ({ code: '', description: '', quantity: 0, qty_unit: 'Each', unit: 'Each', unit_price: 0, cost_factor: null });
const isBlankQuoteLine = (it) => !it || (!String(it.code ?? '').trim() && !String(it.description ?? '').trim() && !(parseFloat(it.unit_price) > 0));
const withoutBlankQuoteLines = (items) => (Array.isArray(items) ? items.filter((it) => !isBlankQuoteLine(it)) : items);

// Product search results list: never narrower than this, so the code, description and stock figures can't overlap.
const SEARCH_DROPDOWN_MIN_W = 940;

// Create Quote shell: [View Price Lists tab] [quote modal, keeps its size] [Price Lists panel slides out on the right].
const QUOTE_MODAL_MAX_W = 1400;
const QUOTE_RAIL_W = 40;
const QUOTE_PANEL_W = 560;
const QUOTE_CUSTOMER_PANEL_W = 680; // the customer's Quotes / Orders / Pricing panels need more room than the B29 price list
const QUOTE_SHELL_GAP = 12;
const QUOTE_LINE_PANEL_W = 540; // the line workspace that slides out beside the quote when a line is clicked
// Units of measure are shown as two characters everywhere on the quote (what is stored and printed is unchanged: Each, M2, Length, M).
const UOM_SHORT = { Each: 'Ea', M2: 'M2', Length: 'Ln', M: 'Lm' };
const uomShort = (u) => UOM_SHORT[u] || String(u || '').slice(0, 2);

// The customer tabs down the left of the Create Quote modal (below the CM tab). Each one slides a Customer Management panel
// out to the right of the quote, exactly like the View Price Lists panel.
const QUOTE_CUSTOMER_TABS = [
  { id: 'quotes', label: 'Quotes', Icon: FileText },
  { id: 'orders', label: 'Orders', Icon: Package },
  { id: 'pricing', label: 'Pricing', Icon: PoundSterling },
];

// The pricing-dropdown data a quote line carries (List Price / Discount / Rebate / Support / Cost Factor ...). It is saved with
// each price on the customer's price list when the price came from a quote, and put back on the line when that price is added
// to a quote again.
const LINE_PRICING_META_KEYS = [
  'margin_config', 'cost_factor', 'pricing_native_uom', 'pricing_updated_label',
  'mf_support_document_id', 'mf_support_document_name',
  'ho_support_matched', 'ho_support_supplier_code', 'ho_support_group',
];
const restoreLineMeta = (meta) => {
  const out = {};
  if (!meta || typeof meta !== 'object') return out;
  LINE_PRICING_META_KEYS.forEach((k) => {
    if (meta[k] !== undefined && meta[k] !== null) out[k] = JSON.parse(JSON.stringify(meta[k]));
  });
  return out;
};

// Shared UOM options for both "Qty UOM" (product/stock unit) and "Price UOM"
// (the unit the price is quoted per) dropdowns on quote/proforma line items.
// Simplified per explicit user request to just the 4 that matter for the Cost
// Factor engine: "Each" (discrete/default), "M2" (area coverage), "Length"
// (linear coverage, Qty side), "M" (linear metre, Price side).
const UOM_OPTIONS = ['Each', 'M2', 'Length', 'M'];

// Product Search Dropdown Component - Full width, dark theme
// `mode="code"` (the Code column of a quote line) calls /stock-database/search-code, which returns only product codes that
// START with what was typed. Without it (the Description column, Add Price) the normal description / keyword / supplier
// search (/stock-database/search) runs, exactly as before.
// Extra props, used by the line workspace only (the quote's own Code and Description boxes leave them off and behave exactly as before):
//   keyboard      arrow keys move down the results, Enter picks the highlighted one (or the first one as soon as the results arrive)
//   onPicked      called after Enter picked a result, so the workspace can move on to the quantity
//   onSubmitText  Enter with nothing found: the typed code is used as it is (a code that is not in stock)
//   focusSignal   put the cursor in the box whenever this number changes
//   inputAttrs    extra attributes for the input (used to find it from the keyboard)
// matchWidth: the results list is exactly as wide as the search box (the line workspace uses it); without it the list is wide enough for code, description and stock.
const ProductSearchDropdown = ({ value, onChange, onSelect, onBlur, placeholder, mode = null, keyboard = false, onPicked = null, onSubmitText = null, focusSignal = 0, inputAttrs = null, matchWidth = false }) => {
  const [searchQuery, setSearchQuery] = useState(value || '');
  const [results, setResults] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showInStockOnly, setShowInStockOnly] = useState(false);
  const [showOtherBranches, setShowOtherBranches] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);
  const searchSeqRef = useRef(0);
  const searchTimerRef = useRef(null);
  const [highlight, setHighlight] = useState(0);
  const debouncePendingRef = useRef(false); // a search is about to be sent (the person is still typing)
  const pendingEnterRef = useRef(false);    // Enter was pressed before the results were back: pick the first one when they arrive

  useEffect(() => {
    setSearchQuery(value || '');
  }, [value]);

  // Anchor the results list directly under THIS search box (Code or Description), and keep it attached while the line
  // items table or the modal scrolls or the window resizes. The measurements are converted to the page's own CSS pixels
  // (getPageZoom) so it lands in the right place on laptops / iPads where the whole page is zoomed to 0.8.
  useEffect(() => {
    if (!isOpen) return undefined;
    const place = () => {
      const el = inputRef.current;
      if (!el) return;
      const zoom = getPageZoom();
      const r = el.getBoundingClientRect();
      const boxLeft = r.left / zoom;
      const viewportW = window.innerWidth / zoom;
      const width = matchWidth ? Math.min(r.width / zoom, viewportW - 16) : Math.min(Math.max(r.width / zoom, SEARCH_DROPDOWN_MIN_W), viewportW - 16);
      const left = Math.max(8, Math.min(boxLeft, viewportW - width - 8));
      setDropdownPosition({ top: r.bottom / zoom + 4, left, width });
    };
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [isOpen, searchQuery]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleInStock = () => {
    const next = !showInStockOnly;
    setShowInStockOnly(next);
    toast.info(next ? 'Showing in-stock only' : 'Showing all products', { duration: 1500 });
  };

  const toggleOtherBranches = () => {
    const next = !showOtherBranches;
    setShowOtherBranches(next);
    toast.info(next ? 'Showing other branches stock' : 'Other branches stock hidden', { duration: 1500 });
  };

  // Keyboard shortcuts while the results list is open: CTRL = In stock, ALT = View other branches. AltGr (which the
  // browser reports as Ctrl + Alt together) is ignored so typing characters like the euro sign doesn't flip them.
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isOpen || e.repeat) return;
      if (e.getModifierState && e.getModifierState('AltGraph')) return;
      if (e.key === 'Control') {
        toggleInStock();
      } else if (e.key === 'Alt') {
        e.preventDefault(); // stops the browser treating a bare Alt press as "focus the menu bar"
        toggleOtherBranches();
      }
    };
    const handleKeyUp = (e) => {
      if (isOpen && e.key === 'Alt') e.preventDefault();
    };
    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('keyup', handleKeyUp);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('keyup', handleKeyUp);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, showInStockOnly, showOtherBranches]);

  // Every keystroke starts a search and the answers can arrive out of order: a slow answer for "25" landing AFTER the
  // answer for "25yj" used to overwrite it, which is why a code sometimes showed something completely different until it
  // was typed again. Each search gets a number and only the newest one is ever allowed to show its results.
  const runSearch = async (query, withBranches) => {
    const seq = ++searchSeqRef.current;
    const cleaned = (query || '').trim();
    if (cleaned.length < 2) {
      setResults([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const response = await api.get(`${API}/api/stock-database/${mode === 'code' ? 'search-code' : 'search'}`, {
        // Other branches' stock is always asked for now: it decides the colour of each result's icon (green: stock here, orange: none here but stock elsewhere, grey: none at all).
        // The branch pills under a result are still only shown when ALT is on.
        params: { q: cleaned, limit: 100, include_all: true, include_other_branches: true }
      });
      if (seq !== searchSeqRef.current) return;
      setResults(response.data?.results || []);
      if (pendingEnterRef.current) {
        pendingEnterRef.current = false;
        const list = (response.data?.results || []).filter((p) => !showInStockOnly || (p.on_hand_quantity || p.on_hand_balance || p.free_stock || 0) > 0 || (p.on_order_quantity || 0) > 0);
        if (list.length > 0) { onSelect(list[0]); setIsOpen(false); setResults([]); if (onPicked) onPicked(); }
        else if (onSubmitText) { setIsOpen(false); onSubmitText(cleaned); if (onPicked) onPicked(); }
      }
    } catch (error) {
      if (seq !== searchSeqRef.current) return;
      console.error('Error searching products:', error);
      setResults([]);
    } finally {
      if (seq === searchSeqRef.current) setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setSearchQuery(newValue);
    onChange(newValue);
    setIsOpen(true);
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    if (newValue.trim().length < 2) {
      searchSeqRef.current += 1; // cancels any search still on its way back
      setResults([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    debouncePendingRef.current = true;
    searchTimerRef.current = setTimeout(() => { debouncePendingRef.current = false; runSearch(newValue, showOtherBranches); }, 200);
  };

  // The Code search only looks up other branches' stock when ALT is on, so switching ALT on runs the search again.
  useEffect(() => {
    if (mode !== 'code' || !isOpen || searchQuery.trim().length < 2) return;
    runSearch(searchQuery, showOtherBranches);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showOtherBranches]);

  useEffect(() => () => {
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchSeqRef.current += 1;
  }, []);

  const handleSelectProduct = (product) => {
    onSelect(product);
    setIsOpen(false);
    setResults([]);
  };

  // Filter results based on stock
  const hasStock = (item) => {
    const inStock = item.on_hand_quantity || item.on_hand_balance || item.free_stock || 0;
    const onOrder = item.on_order_quantity || 0;
    return inStock > 0 || onOrder > 0;
  };

  const filteredResults = showInStockOnly ? results.filter(hasStock) : results;

  useEffect(() => { setHighlight(0); }, [results, showInStockOnly]);
  useEffect(() => {
    if (!focusSignal) return undefined;
    const t = setTimeout(() => { if (inputRef.current) { inputRef.current.focus(); if (inputRef.current.select) inputRef.current.select(); } }, 80);
    return () => clearTimeout(t);
  }, [focusSignal]);
  const handleSearchKeyDown = (e) => {
    if (!keyboard) return;
    const list = filteredResults;
    if (e.key === 'ArrowDown' && isOpen && list.length > 0) { e.preventDefault(); setHighlight((h) => Math.min(h + 1, list.length - 1)); }
    else if (e.key === 'ArrowUp' && isOpen && list.length > 0) { e.preventDefault(); setHighlight((h) => Math.max(h - 1, 0)); }
    else if (e.key === 'Escape') { setIsOpen(false); }
    else if (e.key === 'Enter') {
      const text = searchQuery.trim();
      if (text.length < 2) return;
      if (isOpen && list.length > 0 && !debouncePendingRef.current) {
        e.preventDefault(); e.stopPropagation();
        handleSelectProduct(list[Math.min(highlight, list.length - 1)]);
        if (onPicked) onPicked();
      } else if (debouncePendingRef.current || loading) {
        e.preventDefault(); e.stopPropagation();
        pendingEnterRef.current = true; // the results are still on their way: the first one is picked the moment they arrive
      } else if (onSubmitText) {
        e.preventDefault(); e.stopPropagation();
        setIsOpen(false); onSubmitText(text);
        if (onPicked) onPicked();
      }
    }
  };

  return (
    <div className="relative w-full" ref={dropdownRef}>
      <div className="relative">
        <Input
          ref={inputRef}
          value={searchQuery}
          onChange={handleInputChange}
          onFocus={() => searchQuery.length >= 2 && setIsOpen(true)}
          onBlur={() => { if (onBlur) setTimeout(() => onBlur(searchQuery), 150); }}
          onKeyDown={handleSearchKeyDown}
          {...(inputAttrs || {})}
          placeholder={placeholder}
          className={`pr-8 w-full ${QUOTE_FIELD_CLS}`}
        />
        <Search className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
      </div>
      
      {isOpen && (searchQuery.length >= 2) && (
        <div
          className={`v3-sdd v3-sdd--products ${matchWidth ? 'v3-sdd--narrow' : ''}`}
          style={{ width: `${dropdownPosition.width}px`, top: `${dropdownPosition.top}px`, left: `${dropdownPosition.left}px` }}
          data-testid="quote-search-dropdown"
        >
          {/* Shortcut bar: CTRL = In stock, ALT = View other branches (the keys work, and so do clicks on these) */}
          <div className="v3-sdd-bar">
            <span className="v3-sdd-count">{showInStockOnly ? 'In stock only' : 'All products'} <b>{filteredResults.length}</b></span>
            <span className="v3-sdd-legend" title="S = stock here, F = free stock, O = on order">S stock · F free · O on order</span>
            <div className="v3-sdd-toggles">
              <button
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={toggleInStock}
                aria-pressed={showInStockOnly}
                className={`v3-sdd-toggle ${showInStockOnly ? 'is-green' : ''}`}
                data-testid="quote-search-ctrl-toggle"
              >
                <span className="v3-sdd-key">CTRL</span><span>In stock</span>
              </button>
              <button
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={toggleOtherBranches}
                aria-pressed={showOtherBranches}
                className={`v3-sdd-toggle ${showOtherBranches ? 'is-purple' : ''}`}
                data-testid="quote-search-alt-toggle"
              >
                <span className="v3-sdd-key">ALT</span><span>Other branches</span>
              </button>
            </div>
          </div>
          {/* Results: they scroll inside the panel, which never grows past its own maximum height. */}
          <div className="v3-sdd-list">
            {loading ? (
              <div className="v3-sdd-empty">Searching...</div>
            ) : filteredResults.length > 0 ? (
              filteredResults.map((product, idx) => {
                const inStock = product.on_hand_quantity || product.on_hand_balance || product.free_stock || 0;
                const onOrder = product.on_order_quantity || 0;
                const freeStock = product.free_stock || 0;
                // The icon: green = stock at this branch, orange = none here but stock at another branch, grey = none anywhere in the group.
                const elsewhere = (product.other_branches || []).some((ob) => (ob.on_hand ?? 0) > 0);
                const stockState = inStock > 0 ? 'here' : (elsewhere ? 'elsewhere' : 'none');
                const stockColor = stockState === 'here' ? '#22c55e' : (stockState === 'elsewhere' ? '#ee7203' : '#8c8697');
                return (
                  <div
                    key={idx}
                    className={`v3-sdd-row ${keyboard && idx === highlight ? 'is-hi' : ''}`}
                    onMouseEnter={keyboard ? () => setHighlight(idx) : undefined}
                    onClick={() => handleSelectProduct(product)}
                  >
                    <Package className="v3-sdd-icon" style={{ color: stockColor }} data-stock-state={stockState} />
                    <div className="v3-sdd-body">
                      <div className="v3-sdd-main">
                        <span className="v3-sdd-code">{product.product_code || product.code}</span>
                        <span className="v3-sdd-desc">{product.description || product.name}</span>
                        <span className="v3-sdd-stock">
                          <span className={inStock > 0 ? 'is-on' : ''} title="Stock at this branch">S {inStock}</span>
                          <span className={freeStock > 0 ? 'is-on' : ''} title="Free stock">F {freeStock}</span>
                          <span className={onOrder > 0 ? 'is-on' : ''} title="On order">O {onOrder}</span>
                        </span>
                      </div>
                      {/* Other branches' stock, as pills under the line (same pills as Global Search) - ALT switches them on */}
                          {showOtherBranches && product.other_branches?.length > 0 && (
                            <div className="flex flex-nowrap items-center gap-2 mt-2 overflow-x-auto pb-1" onClick={(e) => e.stopPropagation()}>
                              {product.other_branches.map((ob, obIdx) => {
                                const total = (ob.on_hand ?? 0) - (ob.due_out ?? 0);
                                return (
                                  <span
                                    key={ob.branch_id}
                                    title={`${ob.branch_name} - On hand ${ob.on_hand ?? 0}, In ${ob.incoming ?? 0}, Out ${ob.due_out ?? 0}`}
                                    className={`flex-shrink-0 flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-gray-100 hover:bg-gray-200 hover:ring-2 hover:ring-offset-1 border-2 transition-all cursor-default ${
                                      obIdx % 2 === 0 ? 'border-orange-500 hover:ring-orange-400' : 'border-purple-600 hover:ring-purple-400'
                                    }`}
                                    data-testid={`quote-other-branch-pill-${product.product_code || product.code}-${ob.branch_id}`}
                                  >
                                    <span className="text-[10px] font-bold text-gray-700 underline">{ob.branch_code}</span>
                                    <span className="text-[11px] font-bold text-emerald-600">{total}</span>
                                  </span>
                                );
                              })}
                            </div>
                          )}
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="v3-sdd-empty">No products found for "{searchQuery}"</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Live-typing Customer Name search for Create Quote — queries a LOCAL cache of
// Zoho Accounts (see /api/zoho/accounts/cached-search, refreshed every 3hrs in
// the background) so every keystroke costs zero Zoho API calls. On SELECT, one
// single live Zoho fetch (/accounts/{id}/fresh) pulls the current address so
// what gets auto-filled is always up to date even if the cache is a bit stale.
// fieldClass: draws the box like the quote's other boxes (the header no longer uses the borderless oblong). autoFocus: put the cursor in the box when the quote opens.
const CustomerSearchDropdown = ({ value, onChange, onSelectCustomer, placeholder, borderless = false, fieldClass = null, autoFocus = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetchingAddress, setFetchingAddress] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setIsOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const updateDropdownPosition = () => {
    if (inputRef.current) {
      const rect = inputRef.current.getBoundingClientRect();
      const zoom = getPageZoom();
      setDropdownPosition({ top: rect.bottom / zoom + 4, left: rect.left / zoom, width: Math.max(rect.width / zoom, 360) });
    }
  };

  const search = async (query) => {
    setLoading(true);
    try {
      const res = await api.get(`${API}/api/zoho/accounts/cached-search`, { params: { q: query } });
      setResults(res.data?.accounts || []);
    } catch {
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const val = e.target.value;
    onChange(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (val.length < 2) { setIsOpen(false); return; }
    updateDropdownPosition();
    setIsOpen(true);
    debounceRef.current = setTimeout(() => search(val), 250);
  };

  const handleSelect = async (account) => {
    onChange(account.account_name);
    setIsOpen(false);
    setFetchingAddress(true);
    try {
      const res = await api.get(`${API}/api/zoho/accounts/${account.zoho_id}/fresh`);
      onSelectCustomer({
        address: res.data?.address || '',
        account_name: res.data?.account_name || account.account_name,
        zoho_account_id: account.zoho_id,
        customer_code: res.data?.customer_code || account.customer_code || '',
      });
    } catch {
      toast.error("Couldn't fetch this customer's current address from Zoho");
    } finally {
      setFetchingAddress(false);
    }
  };

  return (
    <div className="relative w-full" ref={dropdownRef}>
      <div className="relative">
        <Input
          ref={inputRef}
          value={value}
          onChange={handleInputChange}
          onFocus={() => { updateDropdownPosition(); if ((value || '').length >= 2) setIsOpen(true); }}
          placeholder={placeholder}
          autoFocus={autoFocus}
          className={fieldClass ? `pr-8 w-full ${fieldClass}` : `pr-8 w-full ${borderless ? 'border-0 shadow-none focus-visible:ring-0 focus-visible:ring-offset-0 h-8 px-1 text-sm font-semibold bg-transparent' : ''}`}
          data-testid="quote-customer-search-input"
        />
        {fetchingAddress ? (
          <Loader2 className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 animate-spin" />
        ) : (
          <Search className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        )}
      </div>

      {isOpen && (value || '').length >= 2 && (
        <div
          className="v3-sdd v3-sdd--customers"
          style={{ width: `${dropdownPosition.width}px`, top: `${dropdownPosition.top}px`, left: `${dropdownPosition.left}px` }}
          data-testid="quote-customer-search-dropdown"
        >
          <div className="v3-sdd-list">
            {loading ? (
              <div className="v3-sdd-empty">Searching CRM customers...</div>
            ) : results.length > 0 ? (
              results.map((account) => (
                <div
                  key={account.zoho_id}
                  className="v3-sdd-row"
                  onClick={() => handleSelect(account)}
                  data-testid={`quote-customer-option-${account.zoho_id}`}
                >
                  <Building2 className="v3-sdd-icon" style={{ color: '#ee7203' }} />
                  <div className="v3-sdd-body">
                    <div className="v3-sdd-code">{account.account_name}</div>
                    {account.billing_city && <div className="v3-sdd-desc">{account.billing_city}</div>}
                  </div>
                </div>
              ))
            ) : (
              <div className="v3-sdd-empty">No CRM match for "{value}" — you can still type a new customer name freely</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};


// ─── Inline Quote Preview (for manual / pdf-upload quotes) ──────────────────
function QuotePreviewModal({ quote, onClose }) {
  const qd = quote.quote_data || {};
  const items = qd.items || [];
  const total = items.reduce((s, i) => s + (parseFloat(i.unit_price) || 0) * (parseFloat(i.quantity) || 0), 0);
  return (
    <div className="fixed inset-0 flex items-start justify-center pt-[5%]"
      style={{ zIndex: 10200, background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(2px)' }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col"
        style={{ width: 'min(96vw, 620px)', maxHeight: '88vh', border: '2px solid #ee7203' }}>
        <div className="flex items-center justify-between px-5 py-4 border-b bg-orange-50">
          <div>
            <span className="text-xs font-bold text-orange-500 uppercase tracking-wide">Quote Preview</span>
            <h2 className="font-bold text-gray-900 text-lg leading-tight">{qd.customer_name || 'Unknown Customer'}</h2>
            <p className="text-xs text-gray-400">{qd.quote_number} · {qd.date}</p>
          </div>
          {quote.zoho_quote_url && (
            <a href={quote.zoho_quote_url} target="_blank" rel="noopener noreferrer"
              className="flex items-center gap-1 text-xs text-blue-600 hover:underline mr-3">
              <ExternalLink className="w-3.5 h-3.5" /> View in CRM
            </a>
          )}
          <button onClick={onClose} className="p-1.5 rounded-full hover:bg-orange-100 text-gray-400"><X className="w-4 h-4" /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {qd.customer_address && (
            <div className="text-sm text-gray-600 bg-gray-50 rounded-xl p-3 whitespace-pre-line">{qd.customer_address}</div>
          )}
          {items.length > 0 && (
            <div className="rounded-xl border border-gray-200 overflow-hidden">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-gray-50 border-b">
                    <th className="px-3 py-2 text-left text-gray-500 font-semibold uppercase tracking-wide">Code</th>
                    <th className="px-3 py-2 text-left text-gray-500 font-semibold uppercase tracking-wide">Description</th>
                    <th className="px-3 py-2 text-right text-gray-500 font-semibold uppercase tracking-wide">Qty</th>
                    <th className="px-3 py-2 text-right text-gray-500 font-semibold uppercase tracking-wide">Unit £</th>
                    <th className="px-3 py-2 text-right text-gray-500 font-semibold uppercase tracking-wide">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item, i) => (
                    <tr key={i} className="border-t border-gray-100 hover:bg-gray-50">
                      <td className="px-3 py-2 font-mono text-gray-700">{item.code || '—'}</td>
                      <td className="px-3 py-2 text-gray-600">{item.description || '—'}</td>
                      <td className="px-3 py-2 text-right text-gray-700">{item.quantity}</td>
                      <td className="px-3 py-2 text-right text-gray-500">£{parseFloat(item.unit_price || 0).toFixed(2)}</td>
                      <td className="px-3 py-2 text-right font-semibold text-gray-700">
                        £{((parseFloat(item.unit_price) || 0) * (parseFloat(item.quantity) || 0)).toFixed(2)}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 border-gray-200 bg-gray-50">
                    <td colSpan={4} className="px-3 py-2 text-right text-xs font-bold text-gray-600 uppercase tracking-wide">Total</td>
                    <td className="px-3 py-2 text-right font-bold text-gray-900">£{total.toFixed(2)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
          {qd.notes && <p className="text-sm text-gray-600 italic">{qd.notes}</p>}
        </div>
        <div className="px-5 py-3 border-t bg-gray-50 flex justify-end">
          <button onClick={onClose}
            className="px-5 py-2 rounded-full text-sm font-semibold text-white hover:opacity-90"
            style={{ backgroundColor: '#ee7203' }}>Close</button>
        </div>
      </div>
    </div>
  );
}

// ─── Source badge config ─────────────────────────────────────────────────────
const SOURCE_CONFIG = {
  manual:     { label: 'Editor',      bg: '#f0fdf4', text: '#15803d', border: '#bbf7d0' },
  pdf:        { label: 'PDF Upload',  bg: '#eff6ff', text: '#1d4ed8', border: '#bfdbfe' },
  automation: { label: 'Automation',  bg: '#fff7ed', text: '#c2410c', border: '#fed7aa' },
};

// M4-parsed automation lines never carry a UOM or Cost Factor from the source
// email (automations.py's extraction only produces qty/unit_price/total_value —
// no qty_unit/stock_unit_price column like the manual PDF-upload extractor).
// Trusting the M4 unit_price as a plain "Each" price and recomputing
// qty × unit_price silently mis-totals any coverage-priced line (M2/length)
// that has no Cost Factor on record. Instead, derive unit_price from the
// parsed line TOTAL so qty × unit_price always reconstructs total_value
// exactly, and mark the line price_locked so nothing downstream (Pricing
// sheet lookups, CRM push) ever overwrites that reconstructed price.
function mapM4LineToQuoteItem(l) {
  const qty = parseFloat(l.qty) || 1;
  const totalValue = (l.total_value != null && l.total_value !== '') ? parseFloat(l.total_value) : null;
  const rawUnitPrice = parseFloat(l.unit_price) || 0;
  const unitPrice = (totalValue != null && qty > 0)
    ? Math.round((totalValue / qty) * 100) / 100
    : rawUnitPrice;
  return {
    code: l.product_code || '',
    description: l.description || '',
    quantity: qty,
    qty_unit: 'Each',
    unit: 'Each',
    unit_price: unitPrice,
    // Honour a Cost Factor if the M4 text genuinely carried one; otherwise
    // left null and backfilled from the Stock Database (see Quotes.jsx's
    // "recent" mode onSelect) — informational only, since unit_price above
    // already guarantees the correct total regardless.
    cost_factor: l.cost_factor != null ? l.cost_factor : null,
    pdf_line_total: totalValue,
    price_locked: true,
  };
}

// ─── Individual Quote Card ───────────────────────────────────────────────────
function QuoteCard({ item, onEdit, onDeleted, onUpdated }) {
  const { requireConfirmed } = useZohoSession();
  const [showCrm, setShowCrm] = useState(false);
  const [showEditCrm, setShowEditCrm] = useState(false);
  const [showLink, setShowLink] = useState(false);
  const [linkedAccount, setLinkedAccount] = useState(null); // { id, name } once the user has linked this quote's customer
  const [viewing, setViewing] = useState(false);
  const [deleting, setDeleting] = useState(false);
  // Track CRM status locally so the card updates immediately after a successful upload.
  // Initialise from the persisted zoho_quote_url if it already exists in the DB.
  const [crmUrl, setCrmUrl] = useState(item.zoho_quote_url || null);
  // Prefer local state (just-added) but fall back to item prop so list-refreshes propagate correctly
  const effectiveCrmUrl = crmUrl || item.zoho_quote_url || null;

  const isAutomation = item.source === 'automation';
  const qd = item.quote_data || {};
  const email = item.email_data;  // set for automation items
  const sc = SOURCE_CONFIG[item.source] || SOURCE_CONFIG.manual;
  // A parsed quote that couldn't be tied to a CRM customer automatically (and isn't in the CRM) is marked NO CRM - ADD NOW.
  const needsCrmLink = isAutomation && !!email && !linkedAccount && !(crmUrl || item.zoho_quote_url)
    && (email.crm_link_needed === true || !email.zoho_account_id);

  const customerName = isAutomation
    ? (email?.invoice_customer_name || email?.customer_name || 'Unknown')
    : (qd.customer_name || 'Unnamed Customer');

  const refNum = isAutomation
    ? (email?.order_number || email?.reference || '—')
    : (qd.quote_number || '—');

  const dateStr = item.last_saved || item.received_at || '';
  const lineCount = isAutomation
    ? (email?.product_lines?.length ?? 0)
    : (qd.items?.length ?? 0);

  const quoteFormForCrm = isAutomation ? (
    item.edited_quote_data || {
      customer_name: email?.invoice_customer_name || email?.customer_name || '',
      customer_address: email?.invoice_address || '',
      quote_number: email?.order_number || '',
      reference: email?.reference || '',
      date: new Date(dateStr).toISOString().split('T')[0],
      valid_until: new Date(Date.now() + 21*24*60*60*1000).toISOString().split('T')[0],
      items: (email?.product_lines || []).map(mapM4LineToQuoteItem),
      notes: '',
      terms: '',
    }
  ) : qd;

  // For automation quotes, trust the sum of each line's own parsed total_value
  // directly — the same figure phocas_auto_add.py uses for its threshold check —
  // rather than recomputing via unit_price × quantity (which is exactly the
  // calculation that silently under/over-totals when a line has no Cost Factor).
  const storedTotal = isAutomation
    ? (email?.product_lines || []).reduce((s, l) => s + (parseFloat(l.total_value) || 0), 0)
    : (qd.grand_total ?? qd.total_price ?? qd.total_value);
  const subtotal = (() => {
    if (storedTotal != null && storedTotal !== '') {
      const n = parseFloat(String(storedTotal).replace(/[£,]/g, ''));
      if (!isNaN(n) && n > 0) return n;
    }
    return (quoteFormForCrm.items || []).reduce((s, i) =>
      s + (parseFloat(i.unit_price) || 0) * (parseFloat(i.quantity) || 0), 0);
  })();

  const handleEdit = () => {
    if (isAutomation) {
      // Pass full item (with id) so onSelect can set _automationEmailId correctly.
      // Prefer edited_quote_data as starting point if already edited.
      const formData = item.edited_quote_data || quoteFormForCrm;
      onEdit({ ...item, quote_data: formData });
    } else {
      onEdit(item);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Remove "${customerName} — ${refNum}" from your recent quotes?`)) return;
    setDeleting(true);
    try {
      await api.delete(`${API}/api/quotes/recent/${item.id}`);
      toast.success('Quote removed from recents');
      onDeleted?.(item.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to remove quote');
    } finally {
      setDeleting(false);
    }
  };

  const handleAddToCrm = () => {
    if (!requireConfirmed()) return;
    setShowCrm(true);
  };

  return (
    <div className={`rounded-2xl border transition-all flex flex-col overflow-hidden ${
      effectiveCrmUrl
        ? 'bg-blue-50 border-blue-200 hover:border-blue-400 hover:shadow-md'
        : 'bg-white border-gray-200 hover:border-orange-200 hover:shadow-md'
    }`}>
      {/* Card header */}
      <div className="px-4 pt-4 pb-3 flex-1">
        <div className="flex items-start justify-between gap-2 mb-2">
          <div className="flex items-center gap-1 flex-wrap">
            <span
              className="text-[10px] font-bold px-2 py-0.5 rounded-full flex-shrink-0"
              style={{ background: sc.bg, color: sc.text, border: `1px solid ${sc.border}` }}
            >{sc.label}</span>
            {item.is_edited && (
              <span
                className="text-[10px] font-bold px-2 py-0.5 rounded-full flex-shrink-0"
                style={{ background: '#f3e8ff', color: '#7c3aed', border: '1px solid #c4b5fd' }}
              >Edited</span>
            )}
          </div>
          <div className="flex flex-col items-end gap-0.5">
            <span className="text-xs text-gray-400 flex-shrink-0">
              {dateStr ? new Date(dateStr).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
            </span>
            {effectiveCrmUrl && (
              <span className="flex items-center gap-0.5 text-[10px] text-blue-500 font-semibold" title="Added to CRM">
                <CheckCircle2 className="w-3 h-3" /> In CRM
              </span>
            )}
            {needsCrmLink && (
              <button
                type="button"
                onClick={() => setShowLink(true)}
                className="flex items-center gap-1 text-[10px] font-extrabold px-2 py-0.5 rounded-full border border-red-300 bg-red-50 text-red-600 hover:bg-red-100 transition-colors"
                title="This customer isn't linked to a CRM account yet — click to link it (it will then match automatically next time)"
                data-testid={`quote-card-no-crm-${item.id}`}
              >
                NO CRM · ADD NOW
              </button>
            )}
          </div>
        </div>
        {effectiveCrmUrl && item.zoho_quote_id && (
          <div className="flex justify-end mb-2">
            <QuoteStagePill
              quoteId={item.zoho_quote_id}
              stage={item.quote_stage}
              onStageChange={(_id, newStage) => onUpdated?.(item.id, { quote_stage: newStage })}
              testIdPrefix="quote-card-stage"
            />
          </div>
        )}
        <h3 className="font-bold text-gray-900 text-sm leading-tight mb-1 truncate">{customerName}</h3>
        <p className="text-xs text-gray-500 font-mono">{refNum}</p>
        <p className="text-xs text-gray-400 mt-1">{lineCount} line{lineCount !== 1 ? 's' : ''}</p>
        {subtotal > 0 && (
          <p className="text-xs font-bold mt-1" style={{ color: '#ee7203' }}>
            £{subtotal.toLocaleString('en-GB', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} ex VAT
          </p>
        )}
      </div>

      {/* 4 action buttons */}
      <div className="border-t border-gray-100 grid grid-cols-4">
        <button
          onClick={handleEdit}
          className="flex flex-col items-center justify-center gap-1 py-3 hover:bg-orange-50 text-gray-600 hover:text-orange-600 transition-colors border-r border-gray-100 text-xs font-semibold"
          data-testid={`quote-card-edit-${item.id}`}
        >
          <Edit2 className="w-3.5 h-3.5" />
          Edit
        </button>

        {effectiveCrmUrl ? (
          <a
            href={effectiveCrmUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex flex-col items-center justify-center gap-1 py-3 transition-colors border-r border-gray-100 text-xs font-semibold"
            style={{ background: '#dbeafe', color: '#1d4ed8' }}
            data-testid={`quote-card-crm-${item.id}`}
          >
            <ExternalLink className="w-3.5 h-3.5" />
            View in CRM
          </a>
        ) : (
          <button
            onClick={handleAddToCrm}
            className="flex flex-col items-center justify-center gap-1 py-3 hover:bg-blue-50 text-gray-600 hover:text-blue-600 transition-colors border-r border-gray-100 text-xs font-semibold"
            data-testid={`quote-card-crm-${item.id}`}
          >
            <Zap className="w-3.5 h-3.5" />
            Add to CRM
          </button>
        )}

        {effectiveCrmUrl && (
          <button
            onClick={() => setShowEditCrm(true)}
            className="flex flex-col items-center justify-center gap-1 py-3 hover:bg-blue-50 text-gray-600 hover:text-blue-600 transition-colors border-r border-gray-100 text-xs font-semibold"
            data-testid={`quote-card-edit-crm-${item.id}`}
          >
            <Settings2 className="w-3.5 h-3.5" />
            Edit CRM
          </button>
        )}

        <button
          onClick={() => setViewing(true)}
          className="flex flex-col items-center justify-center gap-1 py-3 hover:bg-gray-50 text-gray-600 hover:text-gray-800 transition-colors border-r border-gray-100 text-xs font-semibold"
          data-testid={`quote-card-view-${item.id}`}
        >
          <Eye className="w-3.5 h-3.5" />
          View
        </button>

        {/* Delete — removes from user's recents (cleans up duplicates) */}
        {!isAutomation && (
          <button
            onClick={handleDelete}
            disabled={deleting}
            className="flex flex-col items-center justify-center gap-1 py-3 hover:bg-red-50 text-gray-400 hover:text-red-500 transition-colors text-xs font-semibold"
            data-testid={`quote-card-delete-${item.id}`}
            title="Remove from my recents"
          >
            {deleting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
            Remove
          </button>
        )}
        {isAutomation && <div className="border-l border-gray-100" />}
      </div>

      {/* NO CRM - ADD NOW: link this quote's customer to a CRM account, then carry straight on to adding the quote to the CRM */}
      <LinkCrmAccountModal
        isOpen={showLink}
        onClose={() => setShowLink(false)}
        item={item}
        onLinked={(account) => { setLinkedAccount(account); setShowCrm(true); }}
      />

      {/* CRM Modal */}
      {showCrm && (
        <AddToCrmModal
          isOpen={showCrm}
          onClose={() => setShowCrm(false)}
          quoteForm={quoteFormForCrm}
          initialAccount={linkedAccount || undefined}
          pdfFile={null}
          subtotal={subtotal}
          zIndex={10100}
          onCrmSuccess={(url) => setCrmUrl(url)}
        />
      )}

      {/* Edit CRM Modal — amend subject/supplier or add/move project, on the EXISTING Zoho Quote */}
      {showEditCrm && effectiveCrmUrl && (
        <EditCrmQuoteModal
          isOpen={showEditCrm}
          onClose={() => setShowEditCrm(false)}
          quote={{
            zoho_quote_id: item.zoho_quote_id,
            zoho_quote_url: effectiveCrmUrl,
            customer_name: isAutomation ? (email.invoice_customer_name || email.customer_name) : (item.quote_data?.customer_name),
            subject: item.subject,
            supplier_name: item.supplier_name,
            deal_id: item.deal_id,
            deal_name: item.deal_name,
            contractor_package_id: item.contractor_package_id,
            sub_contractor_package_id: item.sub_contractor_package_id,
          }}
          onSaved={(patch) => onUpdated?.(item.id, patch)}
        />
      )}

      {/* View modal */}
      {viewing && (
        isAutomation
          ? <ParsedOrderViewModal order={email} onClose={() => setViewing(false)} />
          : <QuotePreviewModal quote={item} onClose={() => setViewing(false)} />
      )}
    </div>
  );
}

// ─── Recent Quotes View ──────────────────────────────────────────────────────
const ORANGE = '#ee7203';

const ALL_TAB_ID = '__all__';

// Full-text match across customer, ref, postcode and product lines
function itemMatchesSearch(item, query) {
  if (!query.trim()) return true;
  const q = query.toLowerCase();
  const isAuto = item.source === 'automation';
  const qd = item.quote_data || {};
  const email = item.email_data || {};

  const haystack = [
    isAuto ? (email.invoice_customer_name || email.customer_name || '') : (qd.customer_name || ''),
    isAuto ? (email.order_number || email.reference || '') : (qd.quote_number || qd.reference || ''),
    isAuto
      ? [email.delivery_address, email.invoice_address, email.delivery_postcode].filter(Boolean).join(' ')
      : (qd.customer_address || qd.delivery_address || ''),
    item.user_label || '',
  ].join(' ').toLowerCase();

  if (haystack.includes(q)) return true;

  // product lines / items
  const lines = isAuto ? (email.product_lines || []) : (qd.items || []);
  return lines.some(l => {
    const code = ((isAuto ? l.product_code : l.code) || '').toLowerCase();
    const desc = (l.description || '').toLowerCase();
    return code.includes(q) || desc.includes(q);
  });
}

// Lower-cases and strips punctuation so "A W Lumb (Midlands) Ltd" and "a w lumb midlands ltd" compare equal.
const normCustomerName = (s) => (s || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();

// True when a Recent Quotes item belongs to the given customer ({ zohoAccountId, name }); no filter = everything matches.
function itemMatchesCustomer(item, filter) {
  if (!filter) return true;
  const isAuto = item.source === 'automation';
  const qd = item.quote_data || {};
  const email = item.email_data || {};
  const ids = [qd.zoho_account_id, email.zoho_account_id, item.edited_quote_data?.zoho_account_id].filter(Boolean);
  if (filter.zohoAccountId && ids.includes(filter.zohoAccountId)) return true;
  const target = normCustomerName(filter.name);
  if (!target) return false;
  const names = isAuto
    ? [email.invoice_customer_name, email.customer_name, email.zoho_account_name]
    : [qd.customer_name];
  return names.some((n) => normCustomerName(n) === target);
}

const getItemValue = (item) => {
  const qd = item.quote_data || {};
  if (item.source === 'automation') {
    // Sum the parsed total_value directly — never unit_price × qty (silently
    // wrong for coverage-priced lines with no Cost Factor on record).
    const lines = item.email_data?.product_lines || [];
    return lines.reduce((s, l) => s + (parseFloat(l.total_value) || 0), 0);
  }
  const stored = qd.grand_total ?? qd.total_price ?? qd.total_value;
  if (stored != null && stored !== '') {
    const n = parseFloat(String(stored).replace(/[£,]/g, ''));
    if (!isNaN(n) && n > 0) return n;
  }
  return (qd.items || []).reduce((s, i) =>
    s + (parseFloat(i.unit_price) || 0) * (parseFloat(i.quantity) || 0), 0);
};

const RecentQuotesView = ({ onSelect, user, customerFilter = null }) => {
  const [allItems, setAllItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [activeTab, setActiveTab] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      // Customer Management's Quotes panel asks for just this customer's quotes over a rolling 12 months; the Recent Quotes
      // screen (no customer filter) still shows everyone's last 30 days.
      const res = await api.get(`${API}/api/quotes/recent/all`, {
        params: customerFilter ? { zoho_account_id: customerFilter.zohoAccountId || '', customer_name: customerFilter.name || '' } : {},
      }).catch(() => ({ data: { quotes: [], current_user_id: null } }));

      const items = (res.data.quotes || []).map(q => ({
        ...q,
        source: q.source || 'manual',
        _sortDate: q.last_saved,
      })).filter(q => itemMatchesCustomer(q, customerFilter));
      setAllItems(items);

      const uid = res.data.current_user_id || user?.id || null;
      setCurrentUserId(uid);
      // Always open on the current (possibly impersonated) user's tab.
      // Don't fall back to someone else's tab just because they have no quotes.
      setActiveTab(customerFilter ? ALL_TAB_ID : (uid || (items[0]?.user_id || null)));
    } catch {
      toast.error('Failed to load recent quotes');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Build unique user tabs: current user always first (even with no quotes), then alphabetical
  const userTabs = useMemo(() => {
    const map = {};
    allItems.forEach(q => {
      if (!map[q.user_id]) map[q.user_id] = { user_id: q.user_id, label: q.user_label || q.user_id };
    });
    // Always include the current/impersonated user in the tab list, even if they have no quotes
    if (currentUserId && !map[currentUserId]) {
      const label = user?.full_name || user?.username || user?.email || currentUserId;
      map[currentUserId] = { user_id: currentUserId, label };
    }
    return Object.values(map).sort((a, b) => {
      if (a.user_id === currentUserId) return -1;
      if (b.user_id === currentUserId) return 1;
      const aUnknown = !a.label || a.label === 'Unknown';
      const bUnknown = !b.label || b.label === 'Unknown';
      if (aUnknown && !bUnknown) return 1;
      if (bUnknown && !aUnknown) return -1;
      return (a.label || '').localeCompare(b.label || '');
    });
  }, [allItems, currentUserId, user]);

  const baseItems = useMemo(() => {
    const pool = activeTab === ALL_TAB_ID ? allItems : allItems.filter(q => q.user_id === activeTab);
    return [...pool].sort((a, b) => new Date(b._sortDate) - new Date(a._sortDate));
  }, [allItems, activeTab]);

  const visibleItems = useMemo(() =>
    searchQuery.trim() ? baseItems.filter(i => itemMatchesSearch(i, searchQuery)) : baseItems,
    [baseItems, searchQuery]
  );

  // Deduplicate baseItems by quote_number for the total calculation.
  // Copies get a fresh Q-{timestamp} ref so they are never deduped.
  // baseItems is already sorted newest-first so the first occurrence = most recent.
  const { deduplicatedItems, tabTotal } = useMemo(() => {
    const seen = new Set();
    const deduped = [];
    baseItems.forEach(item => {
      const key = (item.quote_data?.quote_number) || item.id;
      if (!seen.has(key)) { seen.add(key); deduped.push(item); }
    });
    const total = deduped.reduce((sum, item) => sum + getItemValue(item), 0);
    return { deduplicatedItems: deduped, tabTotal: total };
  }, [baseItems]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16 gap-3 text-gray-400">
        <Loader2 className="w-5 h-5 animate-spin" />
        <span className="text-sm">Loading quotes…</span>
      </div>
    );
  }

  if (allItems.length === 0) {
    return (
      <div className="text-center py-16 text-gray-400">
        <Clock className="w-12 h-12 mx-auto mb-3 text-gray-200" />
        <p className="font-medium">No quotes in the past month</p>
        <p className="text-sm mt-1">Create a quote or upload a PDF to get started</p>
      </div>
    );
  }

  const isUnknown = (tab) => !tab.label || tab.label === 'Unknown';

  return (
    <div>
      {/* ── User tabs ─────────────────────────────────────────────────────── */}
      {userTabs.length >= 1 && (
        <div className="flex items-end gap-1 border-b border-gray-100 mb-4 overflow-x-auto pb-0" style={{ scrollbarWidth: 'none' }}>
          {/* All tab */}
          <button
            key={ALL_TAB_ID}
            onClick={() => setActiveTab(ALL_TAB_ID)}
            data-testid="tab-all"
            className={`flex-shrink-0 px-3.5 py-2 rounded-t-lg text-xs font-semibold border-t border-l border-r -mb-px transition-colors whitespace-nowrap ${
              activeTab === ALL_TAB_ID ? 'text-white border-orange-400' : 'text-gray-500 bg-gray-50 border-transparent hover:text-gray-900'
            }`}
            style={activeTab === ALL_TAB_ID ? { background: ORANGE, borderColor: ORANGE } : {}}
          >
            All <span className={`ml-1 ${activeTab === ALL_TAB_ID ? 'opacity-80' : 'text-gray-400'}`}>({allItems.length})</span>
          </button>

          {userTabs.map(tab => {
            const isActive = activeTab === tab.user_id;
            const isOwn = tab.user_id === currentUserId;
            const unknown = isUnknown(tab);
            const count = allItems.filter(q => q.user_id === tab.user_id).length;
            const displayLabel = unknown ? 'Unassigned' : tab.label;
            return (
              <button
                key={tab.user_id}
                onClick={() => setActiveTab(tab.user_id)}
                data-testid={`tab-${tab.user_id}`}
                className={`flex-shrink-0 px-3.5 py-2 rounded-t-lg text-xs font-semibold border-t border-l border-r -mb-px transition-colors whitespace-nowrap ${
                  isActive
                    ? unknown ? 'text-white border-amber-400' : 'text-white border-orange-400'
                    : unknown ? 'text-amber-600 bg-amber-50 border-transparent hover:bg-amber-100' : 'text-gray-500 bg-gray-50 border-transparent hover:text-gray-900'
                }`}
                style={isActive ? { background: unknown ? '#d97706' : ORANGE, borderColor: unknown ? '#d97706' : ORANGE } : {}}
              >
                {displayLabel}{isOwn ? ' (me)' : ''} <span className={`ml-1 ${isActive ? 'opacity-80' : unknown ? 'text-amber-400' : 'text-gray-400'}`}>({count})</span>
              </button>
            );
          })}
        </div>
      )}

      {/* ── Search bar ────────────────────────────────────────────────────── */}
      <div className="relative mb-3">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-300 pointer-events-none" />
        <input
          type="text"
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          placeholder="Search customer, quote ref, postcode, product code / description…"
          data-testid="recent-quotes-search"
          className="w-full pl-8 pr-8 py-2 rounded-lg border border-gray-200 text-xs text-gray-700 placeholder-gray-300 focus:outline-none focus:border-orange-300 focus:ring-1 focus:ring-orange-200 bg-gray-50"
        />
        {searchQuery && (
          <button
            onClick={() => setSearchQuery('')}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-300 hover:text-gray-500"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      {/* ── Count + Total + Refresh ────────────────────────────────────────── */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3 flex-wrap">
          <span className="text-sm text-gray-500">
            {visibleItems.length}{searchQuery ? ` of ${baseItems.length}` : ''} item{visibleItems.length !== 1 ? 's' : ''} — {customerFilter ? 'past 12 months' : 'past 30 days'}
            {baseItems.length !== deduplicatedItems.length && (
              <span className="text-xs text-gray-400 ml-1">({deduplicatedItems.length} unique)</span>
            )}
          </span>
          {tabTotal > 0 && (
            <span className="text-sm font-bold px-2.5 py-0.5 rounded-full"
              style={{ background: '#fff3e6', color: '#ee7203' }}>
              £{tabTotal.toLocaleString('en-GB', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} ex VAT
              {baseItems.length !== deduplicatedItems.length && (
                <span className="text-xs font-normal opacity-70 ml-1">(deduped)</span>
              )}
            </span>
          )}
        </div>
        <button onClick={load} className="flex items-center gap-1 text-xs text-gray-400 hover:text-gray-600">
          <RefreshCw className="w-3.5 h-3.5" /> Refresh
        </button>
      </div>

      {visibleItems.length === 0 ? (
        <div className="text-center py-12 text-gray-400">
          <Clock className="w-10 h-10 mx-auto mb-3 text-gray-200" />
          <p className="text-sm">{searchQuery ? `No results for "${searchQuery}"` : 'No quotes for this user in the past month'}</p>
          {searchQuery && <button onClick={() => setSearchQuery('')} className="mt-2 text-xs text-orange-500 hover:underline">Clear search</button>}
        </div>
      ) : (
        <div
          className={`grid grid-cols-2 sm:grid-cols-3 ${customerFilter ? 'xl:grid-cols-4' : 'max-h-[55vh]'} gap-3 overflow-y-auto pr-1 items-start`}
          style={customerFilter ? { maxHeight: 'calc(var(--c30-vh, 100vh) - 300px)' } : undefined}
        >
          {visibleItems.map(item => (
            <QuoteCard
              key={item.id}
              item={item}
              onEdit={onSelect}
              onDeleted={(deletedId) => setAllItems(prev => prev.filter(i => i.id !== deletedId))}
              onUpdated={(id, patch) => setAllItems(prev => prev.map(i => i.id === id ? { ...i, ...patch } : i))}
            />
          ))}
        </div>
      )}
    </div>
  );
};


// Quote Modal Component
// `initialQuote` (optional) opens the editor straight on that Recent Quotes item — used by Customer Management's Quotes tab.
// `prefillAccount` (optional) — { id, name, address } — used by Customer Management's orange Add Quote button so a new
// quote opens already linked to that CRM account, with its name and address filled in, instead of just a bare name.
// `prefillItems` (optional) — [{ code, description, unit_price, unit, price_date, source }] — used by Customer Management's
// Pricing tab "Create Quote": the quote opens with those products already on it at the customer's own prices (one line per
// product, quantity 1), and each line then gets the same cost factor / Settings > Pricing / H/O Support lookups that a
// hand-added line gets.
const QuoteModal = ({ isOpen, onClose, mode, user, prefillCustomer = '', prefillAccount = null, prefillItems = null, resumeSnapshot = null, initialQuote = null, fromCustomerManagement = false }) => {
  const { modalUiV2 } = useUIPreview();
  // UI V3: the window sits in the content area, and tells the shell how much room it wants (it pushes the side bars shut if short).
  // (the V3 window hook is called a little lower, just under the side-panel state, because the width it asks for grows when a side panel slides out)
  const activityBar = useActivityBar();
  const activityIdRef = useRef(resumeSnapshot?.activityId || null);
  const prefillLookupsPendingRef = useRef(false); // true until a prefilled quote's lines have had their lookups run
  const fileInputRef = useRef(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  // Side tabs: open = the quote modal slides left (same size) and ONE panel slides out on its right. The panel is the B29
  // View Price Lists panel ('pricelists') or one of the customer's Quotes / Orders / Pricing panels. `priceListOpen` stays as
  // "any panel is open" because the line layout below tightens up whenever a panel is out.
  const [sidePanel, setSidePanel] = useState(null); // null | 'pricelists' | 'quotes' | 'orders' | 'pricing' | 'line'
  const [activeLine, setActiveLine] = useState(0); // the line open in the line workspace (sidePanel 'line')
  const [lineFocusSignal, setLineFocusSignal] = useState(0); // goes up each time the workspace opens a line, to put the cursor in its search box
  const priceListOpen = sidePanel !== null;
  // UI V3: the window sits in the content area and tells the shell how much room it wants. A side panel (View Price Lists, or the customer's Quotes / Orders /
  // Pricing) makes it wider, so the shell pushes the side bars shut for as long as it is open, exactly as Customer Management does: clicking a bar then opens it
  // over the window, and clicking away from it closes it again.
  const v3w = useV3Window(isOpen, 1300 + (sidePanel === null ? 0 : QUOTE_SHELL_GAP + (sidePanel === 'pricelists' ? QUOTE_PANEL_W : sidePanel === 'line' ? QUOTE_LINE_PANEL_W : QUOTE_CUSTOMER_PANEL_W)));
  const setPriceListOpen = (open) => setSidePanel(open ? 'pricelists' : null);
  // Customer Address: shown as columns (2 lines per column) so all of it is visible; clicking it switches to typing.
  const [editingAddress, setEditingAddress] = useState(false);
  // The window's title icon and colour are those of the Quotes link on the side bar (Settings > User Spaces > Links), so the window matches the button that opens it.
  const { styles: navLinkStyles } = useNavLinkStyles();
  const quoteLinkStyle = navLinkStyles['builtin:quotes'] || {};
  const TitleIcon = ICON_MAP[quoteLinkStyle.icon] || ICON_MAP.FileQuestion || FileText;
  const titleIconColor = quoteLinkStyle.color || DEFAULT_LINK_COLORS['builtin:quotes'] || '#ee7203';
  useEffect(() => { if (!isOpen) setPriceListOpen(false); }, [isOpen]);
  // Add Product Group window (search a code prefix, tick several lines, add them all to the quote).
  const [groupModalOpen, setGroupModalOpen] = useState(false);
  useEffect(() => { if (!isOpen) setGroupModalOpen(false); }, [isOpen]);
  // Keep the original uploaded PDF so we can attach it to a Zoho Quote when the user clicks "Add to CRM"
  const [uploadedPdfFile, setUploadedPdfFile] = useState(null);
  // Add-to-CRM modal state
  const [showCrmModal, setShowCrmModal] = useState(false);
  const { requireConfirmed } = useZohoSession();
  // Gated trigger — opens the Zoho-login prompt first if the user hasn't yet
  // confirmed their CRM session for this browser.
  const handleOpenAddToCrm = async () => {
    if (!requireConfirmed()) return;
    // Pre-save to ensure the DB record exists and _localItemId is populated
    // before AddToCrmModal calls link-quote. Silently ignore failures.
    if (!quoteForm._localItemId && (quoteForm.customer_name || quoteForm.items?.some(i => i.description))) {
      try {
        const res = await api.post(`${API}/api/quotes/autosave`, quoteForm);
        if (res.data.id) {
          setQuoteForm(prev => ({ ...prev, _localItemId: res.data.id }));
        }
      } catch { /* non-fatal */ }
    }
    setShowCrmModal(true);
  };
  const [processing, setProcessing] = useState(false);
  const [sending, setSending] = useState(false);
  const [currentMode, setCurrentMode] = useState(resumeSnapshot?.currentMode || mode); // Track current mode (can switch from upload to create)
  const [prevMode, setPrevMode] = useState(resumeSnapshot?.prevMode || null); // Track mode we came from (e.g. 'recent' → 'create')

  // ── Revise-in-CRM prompt (editing an already-in-CRM Recent Quote) ──────────
  // Snapshot of quote_data taken when entering 'create' via 'recent' — compared
  // against the live quoteForm to detect an actual edit before prompting.
  const originalQuoteFormSnapshotRef = useRef(null);
  const crmPromptResolvedRef = useRef(false);
  const pendingCrmActionRef = useRef(null);
  const [showCrmRevisePrompt, setShowCrmRevisePrompt] = useState(false);
  const [revisingQuote, setRevisingQuote] = useState(false);

  const stripInternalQuoteFields = (qf) => {
    const { _localItemId, _automationEmailId, zoho_quote_id, zoho_quote_url, ...rest } = qf || {};
    // The always-ready empty line at the bottom of the editor is not part of the quote, so it never counts as an edit.
    return Array.isArray(rest.items) ? { ...rest, items: withoutBlankQuoteLines(rest.items) } : rest;
  };

  // Runs `action` immediately unless this quote is already in CRM AND has been
  // edited since load AND hasn't already been prompted this session — in that
  // case it shows the "Update CRM?" modal first and defers `action` until resolved.
  const triggerCrmPromptOrRun = (action) => {
    const needsPrompt = quoteForm.zoho_quote_id
      && originalQuoteFormSnapshotRef.current
      && !crmPromptResolvedRef.current
      && JSON.stringify(stripInternalQuoteFields(quoteForm)) !== originalQuoteFormSnapshotRef.current;
    if (needsPrompt) {
      pendingCrmActionRef.current = action;
      setShowCrmRevisePrompt(true);
    } else {
      action();
    }
  };

  // Margin calculator state
  const [expandedMargin, setExpandedMargin] = useState(null); // Track which item has margin panel open
  const [quickMarginFor, setQuickMarginFor] = useState(null); // Track which item's quick "Add margin" popover is open
  const [applyingBulkMargin, setApplyingBulkMargin] = useState(false);
  const [bulkIncreaseMode, setBulkIncreaseMode] = useState('margin'); // 'margin', 'amount', 'percent'
  const [bulkIncreaseValue, setBulkIncreaseValue] = useState(0);
  const [bulkMarginConfig, setBulkMarginConfig] = useState({
    discount_pct: null, // null = leave each item's own (Pricing-derived) value untouched
    rebate_pct: null,   // null = leave each item's own (Pricing-derived) value untouched
    margin_pct: 0
  });

  // Manufacturer Price Support — documents list (Settings > Pricing > Manufacturer
  // Price Support) used by the per-line MF Support dropdown and the quote-wide
  // "Support Document" selector below.
  const [mfSupportDocs, setMfSupportDocs] = useState([]);
  const [globalSupportDocId, setGlobalSupportDocId] = useState('');
  useEffect(() => {
    api.get(`${API}/api/pricing/mf-support/documents`).then((res) => setMfSupportDocs(res.data?.documents || [])).catch(() => {});
  }, []);
  
  // Reset mode when modal opens (skipped when resuming from the Activity Bar
  // — the restored state below should be kept, not overwritten)
  useEffect(() => {
    if (resumeSnapshot) return;
    setCurrentMode(mode);
    prefillLookupsPendingRef.current = mode === 'create' && Array.isArray(prefillItems) && prefillItems.length > 0;
    if (mode === 'create') {
      // Reset form for new quote — pre-fill customer name if passed in
      setQuoteForm({
        customer_name: prefillAccount?.name || prefillCustomer || '',
        customer_address: prefillAccount?.address || '',
        zoho_account_id: prefillAccount?.id || null,
        quote_number: `Q-${Date.now()}`,
        date: new Date().toISOString().split('T')[0],
        valid_until: new Date(Date.now() + 21*24*60*60*1000).toISOString().split('T')[0],
        items: Array.isArray(prefillItems) && prefillItems.length > 0
          ? prefillItems.map((p) => ({
              code: p.code,
              description: p.description || '',
              quantity: 1,
              qty_unit: 'Each',
              unit: p.unit || 'Each',
              unit_price: parseFloat(p.unit_price) || 0,
              cost_factor: null,
              ...restoreLineMeta(p.line_meta),
              price_locked: true,
              customer_price_date: p.price_date || null,
              customer_price_value: parseFloat(p.unit_price) || 0,
              customer_price_source: p.source || null,
            }))
          : [{ code: '', description: '', quantity: 0, qty_unit: 'Each', unit: 'Each', unit_price: 0, cost_factor: null }],
        notes: '',
        terms: `• All goods and services are supplied in accordance with our standard terms and conditions - these are available from https://www.encon.co.uk/terms-conditions
• We reserve the right to vary prices for individual items if this quotation is not accepted in full.
• This quotation is valid for 21 days from date of issue unless otherwise stated and agreed.
• This quotation may be subject to fluctuations or increases from any manufacturers or suppliers.
• Whilst every attempt has been made to ensure that the prices and information given on this quotation are accurate, Encon take no responsibility for any errors and omissions.
• All prices are subject to VAT, charged at the appropriate rate.`
      });
    }
  }, [mode, isOpen]);
  
  const [quoteForm, setQuoteForm] = useState(resumeSnapshot?.quoteForm || {
    customer_name: '',
    customer_address: '',
    quote_number: `Q-${Date.now()}`,
    date: new Date().toISOString().split('T')[0],
    valid_until: new Date(Date.now() + 21*24*60*60*1000).toISOString().split('T')[0],
    items: [{ code: '', description: '', quantity: 0, qty_unit: 'Each', unit: 'Each', unit_price: 0, cost_factor: null }],
    notes: '',
    terms: `• All goods and services are supplied in accordance with our standard terms and conditions - these are available from https://www.encon.co.uk/terms-conditions
• We reserve the right to vary prices for individual items if this quotation is not accepted in full.
• This quotation is valid for 21 days from date of issue unless otherwise stated and agreed.
• This quotation may be subject to fluctuations or increases from any manufacturers or suppliers.
• Whilst every attempt has been made to ensure that the prices and information given on this quotation are accurate, Encon take no responsibility for any errors and omissions.
• All prices are subject to VAT, charged at the appropriate rate.`
  });

  // Modal UI v2 — register as a resumable "In Progress" activity once the
  // user is actually building/editing a quote (mirrors StockRequestModal).
  useEffect(() => {
    if (!isOpen || !modalUiV2 || currentMode !== 'create' || activityIdRef.current) return;
    activityIdRef.current = resumeSnapshot?.activityId || `quote-${Date.now()}`;
    activityBar.openActivity({
      type: 'quote',
      activityId: activityIdRef.current,
      title: `(Q) - ${quoteForm.customer_name || 'New Quote'}`,
      subtitle: 'Quote',
      snapshot: { activityId: activityIdRef.current, currentMode, prevMode, quoteForm },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, modalUiV2, currentMode]);

  // Keep the live snapshot fresh as the quote is edited.
  useEffect(() => {
    if (!isOpen || !modalUiV2 || !activityIdRef.current) return;
    activityBar.updateActiveSnapshot(
      { activityId: activityIdRef.current, currentMode, prevMode, quoteForm },
      { title: `(Q) - ${quoteForm.customer_name || 'New Quote'}`, subtitle: 'Quote' }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, modalUiV2, quoteForm, currentMode, prevMode]);

  // Ends this quote's Activity Bar tracking — either minimized into "In
  // Progress" (still unfinished, resumable later) or fully discarded (just
  // completed — see the PDF-generated success handler below, which logs it
  // into "Recents" first). The Recent Quotes BROWSE list itself never gets
  // its own tab entry — only actual quote drafts do.
  const endQuoteActivity = (minimize) => {
    if (!modalUiV2 || !activityIdRef.current) return;
    if (minimize) activityBar.minimizeActive(); else activityBar.discardActive();
    activityIdRef.current = null;
  };

  // ── Customer Management tabs + slide-out panels (CM / Quotes / Orders / Pricing) ───────────────────────────────────────
  // The customer panels and Customer Management itself are loaded on demand (dynamic import) so this file never creates an
  // import cycle — Customer Management loads this file on demand too.
  const [saving, setSaving] = useState(false);
  const [cmOpen, setCmOpen] = useState(false);
  const [cmTarget, setCmTarget] = useState({ id: '', name: '' });
  const [custMods, setCustMods] = useState(null);
  useEffect(() => { if (!isOpen) setCmOpen(false); }, [isOpen]);
  useEffect(() => { if (currentMode !== 'create') setSidePanel(null); }, [currentMode]);
  useEffect(() => {
    if (!isOpen || currentMode !== 'create' || custMods) return undefined;
    let alive = true;
    Promise.all([
      import('../components/CustomerQuotesPanel'),
      import('../components/CustomerOrdersPanel'),
      import('../components/CustomerPricingPanel'),
      import('../components/ContactNotesTasksModal'),
    ]).then(([q, o, p, c]) => {
      if (alive) setCustMods({ Quotes: q.default, Orders: o.default, Pricing: p.default, CM: c.ContactNotesTasksModal });
    }).catch(() => { /* non-fatal — the customer tabs simply stay empty */ });
    return () => { alive = false; };
  }, [isOpen, currentMode]); // eslint-disable-line react-hooks/exhaustive-deps

  // The customer the side panels show — debounced so typing a name doesn't reload a panel on every keystroke.
  const [custAccount, setCustAccount] = useState(() => ({ id: quoteForm.zoho_account_id || '', name: (quoteForm.customer_name || '').trim() }));
  useEffect(() => {
    const next = { id: quoteForm.zoho_account_id || '', name: (quoteForm.customer_name || '').trim() };
    const t = setTimeout(() => setCustAccount((prev) => (prev.id === next.id && prev.name === next.name ? prev : next)), 400);
    return () => clearTimeout(t);
  }, [quoteForm.zoho_account_id, quoteForm.customer_name]);

  // Modal UI v2 keeps the header interactive above the overlay — lock page scroll.
  useEffect(() => {
    if (!isOpen || !modalUiV2) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prevOverflow; };
  }, [isOpen, modalUiV2]);

  // Add Product Group: every product ticked in the Add Product Group window goes onto the quote in one go. They fill the ready
  // blank line at the bottom first and carry on below it (a fresh blank line then appears by itself). Each line gets the same
  // lookups as a product picked from the search box (Settings > Pricing list price, the customer's last price, H/O Support),
  // one after the other so a big group doesn't flood the server, with the calculator dropdowns left closed.
  const addProductGroup = (products) => {
    if (!Array.isArray(products) || products.length === 0) return;
    const lines = Array.isArray(quoteForm.items) ? quoteForm.items : [];
    const startIndex = lines.length > 0 && isBlankQuoteLine(lines[lines.length - 1]) ? lines.length - 1 : lines.length;
    const newLines = products.map((p) => ({
      ...newBlankQuoteLine(),
      code: String(p.product_code || p.code || '').trim(),
      description: p.description || p.name || '',
      cost_factor: p.cost_factor != null ? p.cost_factor : null,
    }));
    setExpandedMargin(null);
    setQuoteForm(prev => {
      const current = Array.isArray(prev.items) ? prev.items : [];
      const kept = current.length > 0 && isBlankQuoteLine(current[current.length - 1]) ? current.slice(0, -1) : current;
      return { ...prev, items: [...kept, ...newLines] };
    });
    setGroupModalOpen(false);
    toast.success(`${newLines.length} product${newLines.length === 1 ? '' : 's'} added to the quote`);
    (async () => {
      for (let n = 0; n < newLines.length; n += 1) {
        if (newLines[n].code) {
          await applyPricingLookup(startIndex + n, newLines[n].code, { keepPanelsClosed: true });
        }
      }
    })();
  };

  const removeItem = (index) => {
    if (quoteForm.items.length > 1) {
      setQuoteForm(prev => ({
        ...prev,
        items: prev.items.filter((_, i) => i !== index)
      }));
    }
  };

  // ── The line workspace ──────────────────────────────────────────────────────────────────────────────────────────────────
  // Clicking a line (or its edit button) slides a workspace out on the right that handles that ONE line in full (product search, customer pricing and
  // costing) so the quote itself stays clean. There is no always-ready blank line any more: "Add line" under the table, the workspace's own Add new line
  // button, Enter on the last box of a line, and the + key (from anywhere in the lines or the workspace) all add one and open it. A blank line that is
  // left empty when the workspace is closed is dropped again. Blank lines are never part of the quote (the PDF, the email, the saved quote and Add to CRM
  // all leave them out).
  const openLineEditor = (index) => {
    setActiveLine(index);
    setExpandedMargin(null);
    setQuickMarginFor(null);
    setSidePanel('line');
    setLineFocusSignal((n) => n + 1);
  };
  const addNewLineAndEdit = () => {
    const lines = Array.isArray(quoteForm.items) ? quoteForm.items : [];
    const reuse = lines.length > 0 && isBlankQuoteLine(lines[lines.length - 1]);
    const index = reuse ? lines.length - 1 : lines.length;
    if (!reuse) setQuoteForm((prev) => ({ ...prev, items: [...(Array.isArray(prev.items) ? prev.items : []), newBlankQuoteLine()] }));
    openLineEditor(index);
  };
  const closeLineEditor = () => {
    setQuoteForm((prev) => {
      const current = Array.isArray(prev.items) ? prev.items : [];
      const kept = current.filter((it) => !isBlankQuoteLine(it));
      if (kept.length === current.length) return prev;
      return { ...prev, items: kept.length > 0 ? kept : [newBlankQuoteLine()] };
    });
    setSidePanel(null);
  };
  const goToLine = (index) => {
    const count = Array.isArray(quoteForm.items) ? quoteForm.items.length : 0;
    if (index >= 0 && index < count) openLineEditor(index);
  };
  const addLineRef = useRef(null);
  addLineRef.current = addNewLineAndEdit;

  // "+" on the keyboard adds a new line, from anywhere in the lines or the workspace. (It is left alone in the header boxes, the notes and the other
  // windows, where a + is just a character someone may be typing.)
  useEffect(() => {
    if (!isOpen || currentMode !== 'create') return undefined;
    const onKey = (e) => {
      if ((e.key !== '+' && e.code !== 'NumpadAdd') || e.ctrlKey || e.metaKey || e.altKey) return;
      const t = e.target;
      const tag = t && t.tagName ? t.tagName.toLowerCase() : '';
      if (tag === 'textarea' || (t && t.isContentEditable)) return;
      const inLines = t && t.closest && t.closest('[data-quote-lines], [data-testid="quote-line-editor"]');
      if (!inLines && tag !== 'body') return;
      if (t && t.matches && t.matches('[data-line-field="desc"], input[placeholder^="Description"]')) return; // a description may genuinely contain a +
      e.preventDefault();
      if (addLineRef.current) addLineRef.current();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, currentMode]);

  // A new quote opens the workspace by itself as soon as a customer has been chosen from the CRM (or the quote was started from Customer Management), with the
  // first line already open and the cursor in its search box.
  const lineEditorAutoOpenedRef = useRef(false);
  useEffect(() => {
    if (!isOpen) { lineEditorAutoOpenedRef.current = false; return; }
    if (lineEditorAutoOpenedRef.current || currentMode !== 'create' || initialQuote || resumeSnapshot) return;
    if (!quoteForm.zoho_account_id || !String(quoteForm.customer_name || '').trim()) return;
    const lines = quoteForm.items;
    if (!Array.isArray(lines) || lines.length === 0 || !lines.every(isBlankQuoteLine)) return;
    lineEditorAutoOpenedRef.current = true;
    setActiveLine(0);
    setSidePanel('line');
    setLineFocusSignal((n) => n + 1);
  }, [isOpen, currentMode, quoteForm.zoho_account_id, quoteForm.customer_name, quoteForm.items]); // eslint-disable-line react-hooks/exhaustive-deps


  const updateItem = (index, field, value) => {
    setQuoteForm(prev => ({
      ...prev,
      items: prev.items.map((item, i) => {
        if (i !== index) return item;
        const updated = { ...item, [field]: value };
        // Typing a different price from the customer's own price removes the "last had this price" date under the box.
        if (field === 'unit_price' && updated.customer_price_date && Math.abs((parseFloat(value) || 0) - (parseFloat(item.unit_price) || 0)) > 0.004) {
          updated.customer_price_date = null;
        }
        // The user is directly overriding the price or quantity the PDF was
        // parsed with — abandon the preserved parsed line total (see
        // getLineTotal) and let the normal price × cost factor × qty
        // calculation take over immediately for this line from now on.
        if ((field === 'unit_price' || field === 'quantity') && updated.pdf_line_total != null) {
          updated.pdf_line_total = null;
        }
        // When Price UOM changes, keep the Calculator's "Cost Factor Multiplier"
        // in sync: auto-fill it with the product's cost factor for M2/Length/M
        // (coverage) pricing, reset to 1 for Each (the user can still override).
        if (field === 'unit' && item.margin_config) {
          const cf = parseFloat(updated.cost_factor);
          const validCf = (!isNaN(cf) && cf > 0) ? cf : 1;
          updated.margin_config = {
            ...updated.margin_config,
            uom_multiplier: value !== 'Each' ? validCf : 1
          };
        }
        return updated;
      })
    }));
  };

  // Margin calculator functions
  const updateMarginConfig = (index, field, value) => {
    setQuoteForm(prev => ({
      ...prev,
      items: prev.items.map((item, i) => {
        if (i !== index) return item;
        
        const updatedConfig = {
          ...(item.margin_config || { list_price: 0, uom_multiplier: 1, discount_pct: 0, rebate_pct: 0, mf_support_value: 0, adhoc_adjustment: 0, margin_pct: 0 }),
          [field]: value
        };
        
        // Auto-calculate and apply to unit_price
        const calculatedCost = calculateMarginCost(updatedConfig);
        
        return {
          ...item,
          margin_config: updatedConfig,
          unit_price: calculatedCost
        };
      })
    }));
  };

  const calculateMarginCost = (marginConfig) => {
    if (!marginConfig) return 0;
    const { list_price = 0, uom_multiplier = 1, discount_pct = 0, rebate_pct = 0, mf_support_value = 0, adhoc_adjustment = 0, margin_pct = 0 } = marginConfig;
    
    const afterUOM = list_price * uom_multiplier;  // Apply UOM multiplier first
    const afterDiscount = afterUOM * (1 - discount_pct / 100);
    const afterRebate = afterDiscount * (1 - rebate_pct / 100);
    const afterSupport = afterRebate - (mf_support_value || 0);  // Manufacturer Price Support — pence-per-unit, subtracted after Rebate
    const afterAdhoc = afterSupport + adhoc_adjustment;

    // Margin % is treated as CONTRIBUTION margin on the resulting sell price: e.g.
    // adding 10% margin divides by 0.90, not ×1.10 — this produces a higher sell
    // price than a simple markup for the same %.
    const marginDivisor = 1 - (margin_pct / 100);
    const finalCost = marginDivisor > 0 ? afterAdhoc / marginDivisor : afterAdhoc;

    return Math.round(finalCost * 100) / 100; // 2 decimals
  };

  // Cost basis (list price after UOM/discount/rebate/ad-hoc, BEFORE margin is applied) —
  // this is what the yellow/red price highlighting compares the selling price against.
  const getCostBasis = (marginConfig) => calculateMarginCost({ ...(marginConfig || {}), margin_pct: 0 });

  // 'red'    — there IS cost data and the selling price has dropped below it (Low margin warning)
  // 'yellow' — there IS cost data, price is >= cost, but no margin is being tracked (either never
  //            added, or the price was hand-edited away from what the calculator's margin% produces)
  // 'normal' — no cost data in the system at all, OR a margin is actively tracked and price is healthy
  const getMarginState = (item) => {
    const cost = getCostBasis(item.margin_config);
    if (!cost || cost <= 0) return 'normal';
    const price = parseFloat(item.unit_price) || 0;
    if (price < cost) return 'red';
    const marginPct = item.margin_config?.margin_pct || 0;
    const trackedPrice = marginPct > 0 ? calculateMarginCost(item.margin_config) : null;
    const priceMatchesMargin = trackedPrice != null && Math.abs(price - trackedPrice) < 0.01;
    return priceMatchesMargin ? 'normal' : 'yellow';
  };

  // Calculator panel box colouring — green once a figure is entered so the
  // rep can see at a glance exactly what's feeding the calculation; the
  // Margin box specifically stays yellow (a nudge, not an error) until it's
  // been set, since it's the one figure every line should have.
  const calcBoxClass = (value, { isMargin = false } = {}) => {
    const hasValue = value !== '' && value != null && parseFloat(value) !== 0 && !isNaN(parseFloat(value));
    if (hasValue) return 'border-green-400 bg-green-50 focus-visible:ring-green-400';
    if (isMargin) return 'border-yellow-400 bg-yellow-50 focus-visible:ring-yellow-400';
    return '';
  };

  const applyMarginCost = (index) => {
    const item = quoteForm.items[index];
    const calculatedCost = calculateMarginCost(item.margin_config);
    updateItem(index, 'unit_price', calculatedCost);
    toast.success('Margin applied to unit price');
  };

  const applyBulkMargin = () => {
    if (bulkIncreaseMode === 'amount') {
      // Fixed amount increase
      setQuoteForm(prev => ({
        ...prev,
        items: prev.items.map(item => ({
          ...item,
          unit_price: Math.round((item.unit_price + bulkIncreaseValue) * 100) / 100
        }))
      }));
      toast.success(`Added £${bulkIncreaseValue.toFixed(2)} to all unit prices`);
    } else if (bulkIncreaseMode === 'percent') {
      // Percentage increase
      setQuoteForm(prev => ({
        ...prev,
        items: prev.items.map(item => ({
          ...item,
          unit_price: Math.round((item.unit_price * (1 + bulkIncreaseValue / 100)) * 100) / 100
        }))
      }));
      toast.success(`Increased all unit prices by ${bulkIncreaseValue}%`);
    } else {
      // Margin mode (original functionality)
      setQuoteForm(prev => ({
        ...prev,
        items: prev.items.map(item => {
          // Skip items without list price, and M4-sourced lines whose price is final
          if (!item.margin_config?.list_price || item.price_locked) return item;
          
          const updatedConfig = {
            ...item.margin_config,
            // Only override discount/rebate if the rep explicitly typed a value
            // in the bulk panel — otherwise keep each item's own Pricing-derived
            // discount/rebate intact (previously this always zeroed them out).
            ...(bulkMarginConfig.discount_pct != null ? { discount_pct: bulkMarginConfig.discount_pct } : {}),
            ...(bulkMarginConfig.rebate_pct != null ? { rebate_pct: bulkMarginConfig.rebate_pct } : {}),
            margin_pct: bulkMarginConfig.margin_pct
          };
          
          return {
            ...item,
            margin_config: updatedConfig,
            unit_price: calculateMarginCost(updatedConfig)
          };
        })
      }));
      toast.success('Bulk margin applied to all items with list prices');
    }
    setApplyingBulkMargin(false);
  };

  // Looks up the chosen support document for THIS line's product code and
  // fills MF Support (p) with the pence value found — editable afterwards.
  const applyMfSupportToLine = async (index, doc) => {
    const item = quoteForm.items[index];
    if (!item.code || !item.code.trim()) { toast.error('Enter a product code first'); return; }
    try {
      const res = await api.get(`${API}/api/pricing/mf-support/lookup`, { params: { document_id: doc.id, code: item.code.trim() } });
      if (!res.data?.found) {
        toast.info(`No support found for "${item.code}" in "${doc.name}"`);
        return;
      }
      setQuoteForm(prev => ({
        ...prev,
        items: prev.items.map((it, i) => {
          if (i !== index) return it;
          const updatedConfig = { ...(it.margin_config || {}), mf_support_value: res.data.support_value };
          return {
            ...it,
            margin_config: updatedConfig,
            unit_price: calculateMarginCost(updatedConfig),
            mf_support_document_id: doc.id,
            mf_support_document_name: doc.name,
          };
        })
      }));
      toast.success(`Applied ${(res.data.support_value * 100).toFixed(1)}p support from "${doc.name}"`);
    } catch {
      toast.error('Failed to look up support document');
    }
  };

  // Quote-wide "Support Document" selector (next to Apply Margin/Increase to
  // All) — fills MF Support (p) on every line whose product code is found
  // in the chosen document, leaving non-matching lines untouched.
  const applyGlobalSupportDocument = async (documentId) => {
    setGlobalSupportDocId(documentId);
    if (!documentId) return;
    const doc = mfSupportDocs.find(d => d.id === documentId);
    const codes = quoteForm.items.map(i => (i.code || '').trim()).filter(Boolean);
    if (codes.length === 0) { toast.error('Add product codes to the quote lines first'); return; }
    try {
      const res = await api.post(`${API}/api/pricing/mf-support/lookup-bulk`, { document_id: documentId, codes });
      const results = res.data?.results || {};
      let matched = 0;
      setQuoteForm(prev => ({
        ...prev,
        items: prev.items.map(item => {
          const found = results[(item.code || '').trim().toUpperCase()];
          if (!found) return item;
          matched += 1;
          const updatedConfig = { ...(item.margin_config || {}), mf_support_value: found.support_value };
          return {
            ...item,
            margin_config: updatedConfig,
            unit_price: calculateMarginCost(updatedConfig),
            mf_support_document_id: documentId,
            mf_support_document_name: doc?.name,
          };
        })
      }));
      toast.success(matched > 0 ? `Applied support to ${matched} line(s) from "${doc?.name}"` : `No matching products found in "${doc?.name}"`);
    } catch {
      toast.error('Failed to apply support document');
    }
  };

  const handleProductSelect = (index, product) => {
    setQuoteForm(prev => ({
      ...prev,
      items: prev.items.map((item, i) => i === index ? {
        ...item,
        code: product.product_code || product.code || '',
        description: product.description || product.name || '',
        cost_factor: product.cost_factor != null ? product.cost_factor : item.cost_factor
      } : item)
    }));
    applyPricingLookup(index, product.product_code || product.code || '');
  };

  // View Price Lists panel "+" button: adds that product to the quote, priced at its B29 COST price. The cost goes into the
  // line's pricing dropdown as the List Price (so Discount / Rebate / Margin work from it) and the Unit Price starts at that
  // cost; the dropdown opens so the user can price it accordingly. It goes on the ready blank line at the bottom if there is
  // one, otherwise on a new line. It deliberately does NOT run the Settings > Pricing lookup, which would replace the B29
  // cost with a different list price.
  const addFromPriceList = (product) => {
    const code = String(product.stock_code || product.supplier_product_code || '').trim().toUpperCase();
    const cost = Math.round((parseFloat(product.best_price) || 0) * 100) / 100;
    // The cost is per the product's own price unit: M2 or metre are coverage units, anything else is priced per Each.
    const rawUom = String(product.price_uom || product.order_uom || 'Each').trim().toLowerCase().replace(/[.\s]/g, '');
    const nativeUom = ['m2', 'm²', 'sqm'].includes(rawUom) ? 'M2' : (['m', 'mtr', 'metre', 'meter', 'lm', 'length'].includes(rawUom) ? 'M' : 'Each');
    const marginConfig = { list_price: cost, uom_multiplier: 1, discount_pct: 0, rebate_pct: 0, mf_support_value: 0, adhoc_adjustment: 0, margin_pct: 0 };
    const lines = Array.isArray(quoteForm.items) ? quoteForm.items : [];
    const index = lines.length > 0 && isBlankQuoteLine(lines[lines.length - 1]) ? lines.length - 1 : lines.length;
    const line = {
      ...(lines[index] || newBlankQuoteLine()),
      code,
      description: product.description || code,
      quantity: 1,
      qty_unit: 'Each',
      unit: nativeUom,
      unit_price: calculateMarginCost(marginConfig),
      pricing_native_uom: nativeUom,
      margin_config: marginConfig,
      price_locked: false,
    };
    setQuoteForm(prev => {
      const current = Array.isArray(prev.items) ? prev.items : [];
      const next = [...current];
      next[index] = line;
      return { ...prev, items: next };
    });
    setExpandedMargin(index);
    toast.success(`${code || 'Product'} added at cost £${formatCurrency(cost)}`);
    if (code) {
      // Cost Factor for the line (only applied if the line still holds this same code when the answer arrives).
      api.get(`${API}/api/stock-database/cost-factor/${encodeURIComponent(code)}`).then((res) => {
        if (!res.data?.found) return;
        setQuoteForm(prev => ({
          ...prev,
          items: prev.items.map((it, i) => (i === index && String(it.code || '').trim().toUpperCase() === code ? { ...it, cost_factor: res.data.cost_factor } : it)),
        }));
      }).catch(() => { /* non-fatal - the Cost Factor box can be filled in by hand */ });
      fetchHoSupportSuggestion(index, code);
    }
  };

  // Supplier price list "+" button (View Price Lists > Supplier tab): adds the product to the quote. A product linked to a stock
  // code goes through exactly the lookups that typing or picking that code by hand does, so the line's cost calculator is
  // filled from Settings > Pricing (List Price, Discount, Rebate, Cost Factor, H/O Support). A product with no stock code yet
  // has nothing to look up, so its own list price, discount and rebate are put on the line directly. It goes on the ready blank
  // line at the bottom if there is one, otherwise on a new line.
  const addFromSupplierPriceList = (product) => {
    const code = String(product.stock_code || product.supplier_product_code || '').trim().toUpperCase();
    const linked = !!product.stock_code;
    const nativeUom = product.quote_uom || 'Each';
    const marginConfig = {
      list_price: parseFloat(product.list_price) || 0, uom_multiplier: 1,
      discount_pct: parseFloat(product.discount_pct) || 0, rebate_pct: parseFloat(product.rebate_pct) || 0,
      mf_support_value: 0, adhoc_adjustment: 0, margin_pct: 0,
    };
    const lines = Array.isArray(quoteForm.items) ? quoteForm.items : [];
    const index = lines.length > 0 && isBlankQuoteLine(lines[lines.length - 1]) ? lines.length - 1 : lines.length;
    const line = {
      ...(lines[index] || newBlankQuoteLine()),
      code,
      description: product.description || code,
      quantity: 1,
      qty_unit: 'Each',
      unit: linked ? 'Each' : nativeUom,
      unit_price: linked ? 0 : calculateMarginCost(marginConfig),
      price_locked: false,
      ...(linked ? {} : { pricing_native_uom: nativeUom, margin_config: marginConfig, pricing_updated_label: product.updated_label || '' }),
    };
    setQuoteForm(prev => {
      const current = Array.isArray(prev.items) ? prev.items : [];
      const next = [...current];
      next[index] = line;
      return { ...prev, items: next };
    });
    if (linked) {
      toast.success(`${code} added`);
      lookupCostFactorForCode(index, code);
    } else {
      setExpandedMargin(index);
      toast.success(`${code || 'Product'} added at cost £${formatCurrency(calculateMarginCost(marginConfig))} — it is not linked to a stock code yet`);
      if (code) fetchHoSupportSuggestion(index, code);
    }
  };

  // Customer Pricing panel "+" button: adds that product to the quote at the customer's own price. When the price originally
  // came from a quote, the pricing dropdown it was built with (List Price, Discount, Rebate, Support, Cost Factor ...) is put
  // back on the line and the dropdown opens, so the line looks exactly as it did when it was quoted. It goes on the ready
  // blank line at the bottom if there is one, otherwise on a new line.
  const addFromCustomerPriceList = (price) => {
    const code = String(price?.code || '').trim().toUpperCase();
    if (!code) return;
    const unitPrice = Math.round((parseFloat(price.unit_price) || 0) * 100) / 100;
    const restored = restoreLineMeta(price.line_meta);
    const lines = Array.isArray(quoteForm.items) ? quoteForm.items : [];
    const index = lines.length > 0 && isBlankQuoteLine(lines[lines.length - 1]) ? lines.length - 1 : lines.length;
    const line = {
      ...(lines[index] || newBlankQuoteLine()),
      ...restored,
      code,
      description: price.description || code,
      quantity: 1,
      qty_unit: 'Each',
      unit: price.unit || 'Each',
      unit_price: unitPrice,
      price_locked: true,
      customer_price_date: price.price_date || null,
      customer_price_value: unitPrice,
      customer_price_source: price.source || null,
    };
    setQuoteForm(prev => {
      const current = Array.isArray(prev.items) ? prev.items : [];
      const next = [...current];
      next[index] = line;
      return { ...prev, items: next };
    });
    if (restored.margin_config) setExpandedMargin(index);
    toast.success(`${code} added at £${formatCurrency(unitPrice)}`);
    if (restored.margin_config && restored.cost_factor == null) {
      api.get(`${API}/api/stock-database/cost-factor/${encodeURIComponent(code)}`).then((res) => {
        if (!res.data?.found) return;
        setQuoteForm(prev => ({
          ...prev,
          items: prev.items.map((it, i) => (i === index && String(it.code || '').trim().toUpperCase() === code ? { ...it, cost_factor: res.data.cost_factor } : it)),
        }));
      }).catch(() => { /* non-fatal - the Cost Factor box can be filled in by hand */ });
    }
    // A price that did not bring its own pricing dropdown with it (it came from an order, or was added by hand) gets exactly the lookups a product typed or picked by hand gets:
    // the Cost Factor, Settings > Pricing (List Price, Discount, Rebate), the customer's price and the H/O Support suggestion, so the line's cost calculator is filled in
    // (and its dropdown opens). The customer's price is locked on the line, so these only fill in the cost data and never change the price.
    if (!restored.margin_config) lookupCostFactorForCode(index, code);
  };

  // Customer price list (Customer Management > Pricing): when this quote's customer has the product on their list, the Unit
  // Price is pre-filled with the price they last had (quoted or bought) and that date shows in small text under the box.
  // It runs alongside the Settings > Pricing lookup below and the customer's own price wins.
  const applyCustomerPrice = async (index, code) => {
    const name = (quoteForm.customer_name || '').trim();
    const accountId = quoteForm.zoho_account_id || '';
    if (!code || !code.trim() || (!name && !accountId)) return;
    try {
      const res = await api.get(`${API}/api/customer-pricing/lookup`, { params: { account_id: accountId, name, code: code.trim() } });
      if (!res.data?.found) return;
      const { unit_price, unit, price_date, source } = res.data;
      setQuoteForm(prev => ({
        ...prev,
        items: prev.items.map((item, i) => {
          if (i !== index || (item.code || '').trim().toUpperCase() !== code.trim().toUpperCase()) return item;
          return {
            ...item,
            unit: unit || item.unit || 'Each',
            unit_price,
            price_locked: true,
            customer_price_date: price_date,
            customer_price_value: unit_price,
            customer_price_source: source,
          };
        })
      }));
    } catch { /* non-fatal — no customer price on file for this product */ }
  };

  // Looks up Settings > Pricing (List Price, Price UOM, and this product's
  // group Discount%/Rebate%) for a selected/typed product code and auto-fills
  // the line's Calculator panel + Price UOM — mirrors the Cost Factor lookup
  // below. Non-fatal if the code isn't in the Pricing sheet yet.
  const applyPricingLookup = async (index, code, opts = {}) => {
    if (!code || !code.trim()) return;
    applyCustomerPrice(index, code);
    fetchHoSupportSuggestion(index, code);
    try {
      const res = await api.get(`${API}/api/pricing/lookup/${encodeURIComponent(code.trim())}`);
      if (!res.data?.found) return;
      // A priced product was matched — auto-open this line's Calculator panel so the
      // user immediately sees List Price/Cost Factor/Discount/Rebate, closing whatever
      // other line's panel was open (accordion: only one panel open at a time).
      if (!opts.keepPanelsClosed) setExpandedMargin(index);
      const { list_price, quote_uom, discount_pct, rebate_pct, updated_label } = res.data;
      setQuoteForm(prev => ({
        ...prev,
        items: prev.items.map((item, i) => {
          if (i !== index) return item;
          const existingConfig = item.margin_config || { list_price: 0, uom_multiplier: 1, discount_pct: 0, rebate_pct: 0, mf_support_value: 0, adhoc_adjustment: 0, margin_pct: 0 };
          const cf = parseFloat(item.cost_factor);
          const hasCf = !isNaN(cf) && cf > 0;

          // M4-sourced lines already carry a FINAL price from the Sales Order
          // Acknowledgement — a Pricing sheet lookup (triggered by re-typing/correcting
          // the code, or by the automatic backfill on PDF import) must NEVER overwrite
          // that price or its Price UOM. We still refresh the cost-basis data (so the
          // yellow/red margin flag keeps working) using the item's EXISTING unit.
          if (item.price_locked) {
            const currentUnit = item.unit || 'Each';
            let uomMultiplier = 1;
            if (quote_uom && currentUnit !== quote_uom) {
              if (quote_uom !== 'Each' && currentUnit === 'Each') uomMultiplier = hasCf ? cf : 1;
              else if (quote_uom === 'Each' && currentUnit !== 'Each') uomMultiplier = hasCf ? 1 / cf : 1;
            }
            const lockedConfig = { ...existingConfig, list_price, discount_pct, rebate_pct, uom_multiplier: uomMultiplier };
            return {
              ...item,
              pricing_native_uom: quote_uom,
              margin_config: lockedConfig,
              pricing_updated_label: updated_label,
              // unit_price / unit intentionally untouched — M4 price is final.
            };
          }

          // Price UOM always defaults to "Each" (per explicit spec). If the List
          // Price's native UOM is a coverage unit (M2 or M) and this product has a
          // Cost Factor, pre-multiply the list price by it to get the each price
          // (uom_multiplier = cf). If there's no Cost Factor yet, default to ×1
          // (the raw native-unit price is shown as-is until a CF is available —
          // see handlePriceUomChange for what happens when the user later switches
          // Price UOM to the native coverage unit: multiplier resets to 1 there).
          const needsCfDefault = quote_uom !== 'Each' && hasCf;
          const uomMultiplier = needsCfDefault ? cf : 1;
          const updatedConfig = { ...existingConfig, list_price, discount_pct, rebate_pct, uom_multiplier: uomMultiplier };
          return {
            ...item,
            unit: 'Each',
            pricing_native_uom: quote_uom, // remembers what UOM this List Price is actually denominated in
            margin_config: updatedConfig,
            unit_price: calculateMarginCost(updatedConfig),
            pricing_updated_label: updated_label,
          };
        })
      }));
    } catch { /* non-fatal — product simply isn't priced in Settings > Pricing yet */ }
  };

  // Auto-suggests an H/O Support % (Quotes pricing dropdown, between Discount
  // and Rebate) for this line's product code — same product->supplier->group
  // matching as the Order Book H/O Support pill, taking the HIGHER end of the
  // matrix's claim range. Suggestion only; nothing is applied until the rep
  // clicks Apply (see toggleHoSupportApply below). No match -> blank, manually
  // typed box instead. Non-fatal either way.
  const fetchHoSupportSuggestion = async (index, code) => {
    if (!code || !code.trim()) return;
    try {
      const res = await api.get(`${API}/api/order-book-reports/ho-support/product-lookup`, { params: { code: code.trim() } });
      setQuoteForm(prev => ({
        ...prev,
        items: prev.items.map((item, i) => {
          if (i !== index) return item;
          if (item.margin_config?.ho_support_applied) return item; // already applied — don't clobber
          const existingConfig = item.margin_config || { list_price: 0, uom_multiplier: 1, discount_pct: 0, rebate_pct: 0, mf_support_value: 0, adhoc_adjustment: 0, margin_pct: 0 };
          return {
            ...item,
            ho_support_matched: !!res.data?.matched,
            ho_support_supplier_code: res.data?.supplier_code || null,
            ho_support_group: res.data?.group || null,
            margin_config: { ...existingConfig, ho_support_pct: res.data?.matched ? Math.round(res.data.suggested_pct * 10000) / 100 : null },
          };
        })
      }));
    } catch { /* non-fatal — line just shows a blank, manually-entered H/O Support box */ }
  };

  // Applies (or reverts) the H/O Support % box: on Apply, computes the £ amount
  // off List-less-Discount (BEFORE Rebate — this is claimed back as a cost of
  // sale, so it's worked out on the pre-rebate figure) and subtracts it into the
  // EXISTING Ad-hoc box (same place/order Ad-hoc already sits in the pricing
  // formula); clicking Apply again reverts exactly that amount back out.
  const toggleHoSupportApply = (index) => {
    setQuoteForm(prev => ({
      ...prev,
      items: prev.items.map((item, i) => {
        if (i !== index) return item;
        const cfg = item.margin_config || {};
        if (cfg.ho_support_applied) {
          const revertedAdhoc = Math.round(((cfg.adhoc_adjustment || 0) + (cfg.ho_support_applied_amount || 0)) * 100) / 100;
          const updatedConfig = { ...cfg, ho_support_applied: false, ho_support_applied_amount: 0, adhoc_adjustment: revertedAdhoc };
          return { ...item, margin_config: updatedConfig, unit_price: calculateMarginCost(updatedConfig) };
        }
        const pct = parseFloat(cfg.ho_support_pct) || 0;
        if (pct <= 0) { toast.error('Enter a H/O Support % first'); return item; }
        const afterUOM = (cfg.list_price || 0) * (cfg.uom_multiplier || 1);
        const afterDiscount = afterUOM * (1 - (cfg.discount_pct || 0) / 100);
        const amount = Math.round(afterDiscount * (pct / 100) * 100) / 100;
        const newAdhoc = Math.round(((cfg.adhoc_adjustment || 0) - amount) * 100) / 100;
        const updatedConfig = { ...cfg, ho_support_applied: true, ho_support_applied_amount: amount, adhoc_adjustment: newAdhoc };
        return { ...item, margin_config: updatedConfig, unit_price: calculateMarginCost(updatedConfig) };
      })
    }));
  };

  // Recomputes the pricing engine's uom_multiplier when the user changes Price UOM
  // away from (or back to) the priced product's own native UOM (see applyPricingLookup):
  //   target === native            -> ×1  (list price already IS in that unit)
  //   native M2/M, target Each     -> ×cf (each price = coverage price × cost factor)
  //   native Each, target M2/M     -> ÷cf (coverage price = each price ÷ cost factor)
  // Looks up the Cost Factor Master if it isn't already on the item.
  const handlePriceUomChange = async (index, newUnit) => {
    const item = quoteForm.items[index];
    const nativeUom = item?.pricing_native_uom;

    if (!nativeUom || !item.margin_config) {
      updateItem(index, 'unit', newUnit); // no Pricing data tracked for this line — plain unit change
      return;
    }

    let cf = parseFloat(item.cost_factor);
    if ((isNaN(cf) || cf <= 0) && item.code) {
      try {
        const res = await api.get(`${API}/api/stock-database/cost-factor/${encodeURIComponent(item.code)}`);
        if (res.data?.found) cf = res.data.cost_factor;
      } catch { /* fall back to existing/1 below */ }
    }
    const validCf = (!isNaN(cf) && cf > 0) ? cf : 1;

    let uomMultiplier = 1;
    if (newUnit === nativeUom) uomMultiplier = 1;
    else if (nativeUom !== 'Each' && newUnit === 'Each') uomMultiplier = validCf;
    else if (nativeUom === 'Each' && newUnit !== 'Each') uomMultiplier = 1 / validCf;

    setQuoteForm(prev => ({
      ...prev,
      items: prev.items.map((it, i) => {
        if (i !== index) return it;
        const updatedConfig = { ...it.margin_config, uom_multiplier: uomMultiplier };
        // M4-locked lines: convert the EXISTING final unit_price by the same UOM
        // ratio (×cf / ÷cf) instead of recomputing from the Pricing sheet's list
        // price — the £ value must stay tied to what M4 actually charged.
        const priceRatio = it.unit === newUnit ? 1
          : (it.unit !== 'Each' && newUnit === 'Each') ? validCf
          : (it.unit === 'Each' && newUnit !== 'Each') ? 1 / validCf
          : 1;
        const nextUnitPrice = it.price_locked
          ? Math.round((parseFloat(it.unit_price) || 0) * priceRatio * 100) / 100
          : calculateMarginCost(updatedConfig);
        return {
          ...it,
          unit: newUnit,
          cost_factor: (!isNaN(cf) && cf > 0) ? cf : it.cost_factor,
          margin_config: updatedConfig,
          unit_price: nextUnitPrice,
        };
      })
    }));
  };

  // Look up the M2/coverage cost factor for a manually-typed product code
  // (dropdown selections already carry cost_factor via handleProductSelect).
  const lookupCostFactorForCode = async (index, code, opts = {}) => {
    if (!code || !code.trim()) return;
    try {
      const res = await api.get(`${API}/api/stock-database/cost-factor/${encodeURIComponent(code.trim())}`);
      if (res.data?.found) {
        updateItem(index, 'cost_factor', res.data.cost_factor);
      }
    } catch { /* non-fatal — leave cost factor as-is */ }
    applyPricingLookup(index, code, opts);
  };

  // Prefilled quote (Customer Management > Pricing > Create Quote): once the form holds the prefilled lines, run the same
  // lookups a hand-added line gets. The customer's own price is locked on every prefilled line (price_locked), so these
  // never overwrite it; they only fill in the cost factor, the Settings > Pricing calculator data and the H/O Support
  // suggestion. Runs once per opening of the modal.
  useEffect(() => {
    if (!prefillLookupsPendingRef.current || !Array.isArray(prefillItems)) return;
    const lines = quoteForm.items || [];
    if (lines.length !== prefillItems.length || !lines.every((it) => it.price_locked)) return;
    prefillLookupsPendingRef.current = false;
    lines.forEach((line, index) => {
      // A line that came back with its own saved pricing dropdown (margin_config) keeps it exactly as it was quoted.
      if (line.margin_config) return;
      lookupCostFactorForCode(index, line.code, { keepPanelsClosed: true });
    });
  }, [quoteForm]); // eslint-disable-line react-hooks/exhaustive-deps

  // Loads a Recent Quotes item into the editor. `fromList` is true when it was picked in this modal's own Recent Quotes
  // list (closing the editor then goes back to that list); false when opened straight from a customer's Quotes tab.
  const loadQuoteFromRecent = (quote, fromList = true) => {
    const isAutomationItem = quote.source === 'automation' || quote.source === 'automation_email';
    if (isAutomationItem) {
      // Route saves to automation_emails document instead of creating a new record
      setQuoteForm({ ...quote.quote_data, _automationEmailId: quote.id });
      // Backfill Cost Factor from the Stock Database for any M4-parsed
      // line that doesn't already have one — informational only, since
      // mapM4LineToQuoteItem already derived a total-consistent
      // unit_price regardless of whether a Cost Factor is found.
      (quote.quote_data?.items || []).forEach((it, idx) => {
        if (it.code && it.cost_factor == null) {
          lookupCostFactorForCode(idx, it.code);
        }
      });
    } else {
      setQuoteForm({
        ...quote.quote_data,
        _localItemId: quote.id,
        zoho_quote_id: quote.zoho_quote_id || null,
        zoho_quote_url: quote.zoho_quote_url || null,
      });
      originalQuoteFormSnapshotRef.current = quote.zoho_quote_id ? JSON.stringify(stripInternalQuoteFields(quote.quote_data || {})) : null;
      crmPromptResolvedRef.current = false;
    }
    setPrevMode(fromList ? 'recent' : null);
    setCurrentMode('create');
    toast.success('Quote loaded — continue editing');
  };

  // Opened with a specific quote (Customer Management > Quotes tab): load it straight into the editor.
  useEffect(() => {
    if (isOpen && initialQuote) loadQuoteFromRecent(initialQuote, false);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Cost Factor engine — converts M2/Length (coverage) quantities & prices into
  // stock-unit totals. See the "Qty UOM" / "Price UOM" columns: when a UOM is a
  // coverage unit (M2 or M — anything other than "Each"), the product's Cost
  // Factor (coverage covered per stock unit) is applied.
  const hasValidCostFactor = (item) => {
    const cf = parseFloat(item.cost_factor);
    return !isNaN(cf) && cf > 0;
  };
  const getItemCostFactor = (item) => (hasValidCostFactor(item) ? parseFloat(item.cost_factor) : 1);

  // Qty(Each) — the number of stock units needed. Only derived from the cost
  // factor when the Qty UOM is a coverage unit (M2/Length); otherwise the
  // entered quantity already IS the stock-unit count.
  const getQtyEach = (item) => {
    const qty = parseFloat(item.quantity) || 0;
    const qtyUnit = item.qty_unit || 'Each';
    if (qtyUnit !== 'Each') {
      return Math.ceil(qty / getItemCostFactor(item));
    }
    return qty;
  };

  // Line Total — a coverage Price UOM (M2 or M) applies the cost factor
  // (coverage price × cost factor × qty each); "Each" just multiplies price ×
  // qty each directly. When the Price UOM is a coverage unit but there's no
  // Cost Factor on record for this product yet (shown as "N/A" in the Cost
  // Factor column), price × quantity has no reliable meaning — e.g. £4.23/M2
  // is NOT £4.23 each — so trust whatever total the source PDF itself parsed
  // for this exact line instead of guessing. The moment a Cost Factor becomes
  // known (looked up from Stock Database, or typed in manually) this falls
  // through to the real calculation again; the moment the user edits price or
  // quantity themselves, pdf_line_total is cleared and this always recalculates.
  const getLineTotal = (item) => {
    const priceUnit = item.unit || 'Each';
    if (priceUnit !== 'Each' && !hasValidCostFactor(item) && item.pdf_line_total != null) {
      return Math.round(parseFloat(item.pdf_line_total) * 100) / 100;
    }
    const qtyEach = getQtyEach(item);
    const price = parseFloat(item.unit_price) || 0;
    const cf = priceUnit !== 'Each' ? getItemCostFactor(item) : 1;
    return Math.round(price * cf * qtyEach * 100) / 100;
  };

  const calculateSubtotal = () => quoteForm.items.reduce((sum, item) => sum + getLineTotal(item), 0);
  const calculateVAT = () => calculateSubtotal() * 0.2;
  const calculateTotal = () => calculateSubtotal() + calculateVAT();

  // Adds this quote's prices to the customer's price list (Customer Management > Pricing). Prices only ever go onto the list
  // when the user FINISHES with the quote — Save, Generate & Download PDF or Send to My Email. Just closing the modal leaves
  // the quote open in Recent Quotes and its prices off the customer's list until one of those three is used.
  const commitQuotePrices = async () => {
    try {
      const res = await api.post(`${API}/api/quotes/autosave`, quoteForm);
      if (quoteForm._automationEmailId) {
        await api.post(`${API}/api/quotes/recent/${quoteForm._automationEmailId}/commit-prices`, null, { params: { source: 'automation' } });
        return;
      }
      const id = res.data?.id || quoteForm._localItemId;
      if (id) {
        // list_price is the quote's ex-VAT subtotal as shown on screen - the CRM quote value when the quote is auto-added to the CRM.
        await api.post(`${API}/api/quotes/recent/${id}/commit-prices`, null, { params: { list_price: calculateSubtotal() } });
        if (!quoteForm._localItemId) setQuoteForm(prev => ({ ...prev, _localItemId: id }));
      }
    } catch (err) {
      console.warn('Committing quote prices failed:', err);
      toast.error("Quote saved, but its prices could not be added to the customer's price list");
    }
  };

  // Save: finishes the quote — saves it as completed, adds its prices to the customer's price list and closes it, going back
  // to wherever the user came from (Recent Quotes list, Customer Management, or just closing for a new quote).
  const saveQuoteAndClose = () => {
    if (!quoteForm.customer_name.trim()) {
      toast.error('Please enter a customer name');
      return;
    }
    if (quoteForm.items.length === 0 || !quoteForm.items.some(i => i.description)) {
      toast.error('Please add at least one item with a description');
      return;
    }
    triggerCrmPromptOrRun(doSaveQuoteAndClose);
  };

  const doSaveQuoteAndClose = async () => {
    try {
      setSaving(true);
      await commitQuotePrices();
      window.dispatchEvent(new Event('c30:quotes-changed'));
      toast.success("Quote saved — its prices are now on the customer's price list");
      if (modalUiV2 && activityIdRef.current) {
        activityBar.logRecentView({
          type: 'quote',
          activityId: activityIdRef.current,
          title: `(Q) - ${quoteForm.customer_name || 'Quote'}`,
          subtitle: 'Completed',
          snapshot: { activityId: activityIdRef.current, currentMode: 'create', prevMode, quoteForm },
        });
      }
      endQuoteActivity(false);
      if (prevMode === 'recent') {
        setPrevMode(null);
        setCurrentMode('recent');
      } else {
        onClose();
      }
    } finally {
      setSaving(false);
    }
  };

  const generateAndDownloadPDF = async () => {
    if (!quoteForm.customer_name.trim()) {
      toast.error('Please enter a customer name');
      return;
    }
    if (quoteForm.items.length === 0 || !quoteForm.items.some(i => i.description)) {
      toast.error('Please add at least one item with a description');
      return;
    }
    triggerCrmPromptOrRun(doGenerateAndDownloadPDF);
  };

  const doGenerateAndDownloadPDF = async () => {
    try {
      setProcessing(true);
      
      // Persist the latest amendments to Recent Quotes BEFORE generating
      // the PDF. The autosave interval only fires every 30s, so users who
      // tweak a recent quote and immediately download could otherwise see
      // their PDF reflect changes that the Recent Quotes list doesn't.
      // Failure here is non-fatal — the PDF should still download.
      // Saves the quote AND adds its prices to the customer's price list (a downloaded PDF counts as finishing the quote).
      // Failure here is non-fatal — the PDF should still download.
      await commitQuotePrices();

      const response = await api.post(`${API}/api/quotes/generate-pdf-only`, {
        ...quoteForm,
        subtotal: calculateSubtotal(),
        vat: calculateVAT(),
        total: calculateTotal()
      }, {
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Quote_${quoteForm.quote_number}_${quoteForm.customer_name.replace(/[^a-zA-Z0-9]/g, '_')}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success('Quote PDF downloaded successfully!');
      window.dispatchEvent(new Event('c30:quotes-changed'));
      // Completed (PDF generated) — moves from "In Progress" into "Recents"
      // as this specific quote, not the Recent Quotes browse page itself.
      if (modalUiV2 && activityIdRef.current) {
        activityBar.logRecentView({
          type: 'quote',
          activityId: activityIdRef.current,
          title: `(Q) - ${quoteForm.customer_name || 'Quote'}`,
          subtitle: 'Completed',
          snapshot: { activityId: activityIdRef.current, currentMode: 'create', prevMode, quoteForm },
        });
      }
      endQuoteActivity(false);
      onClose();
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast.error('Failed to generate PDF. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  const sendToMyEmail = async () => {
    if (!quoteForm.customer_name.trim()) {
      toast.error('Please enter a customer name');
      return;
    }
    if (quoteForm.items.length === 0 || !quoteForm.items.some(i => i.description)) {
      toast.error('Please add at least one item with a description');
      return;
    }
    triggerCrmPromptOrRun(doSendToMyEmail);
  };

  const doSendToMyEmail = async () => {
    try {
      setSending(true);
      // Saves the quote AND adds its prices to the customer's price list (emailing the quote counts as finishing it).
      await commitQuotePrices();
      const response = await api.post(`${API}/api/quotes/email-self`, {
        ...quoteForm,
        subtotal: calculateSubtotal(),
        vat: calculateVAT(),
        total: calculateTotal()
      });
      toast.success(response.data?.message || 'Quote emailed to you successfully!');
    } catch (error) {
      console.error('Error emailing quote:', error);
      toast.error(error.response?.data?.detail || 'Failed to email quote. Please try again.');
    } finally {
      setSending(false);
    }
  };

  const handleFileUpload = async (file) => {
    if (!file) return;
    
    try {
      setProcessing(true);
      const formData = new FormData();
      formData.append('file', file);

      // Call extract endpoint to get JSON data
      const response = await api.post(`${API}/api/quotes/extract-pdf`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response.data.success && response.data.data) {
        const extracted = response.data.data;
        
        // Map extracted items → form items.
        // For PDFs that include a "Disc %" + "Nett Total" column (Encon Quotation format),
        // we collapse each line to a single row whose **unit_price = nett line total** and
        // **quantity = 1**, so the UI Unit Price column matches the PDF's NETT TOTAL column
        // exactly. Original qty/UOM/list/discount are preserved as a read-only note under
        // the price. Margin_config stays blank so this doesn't interact with the margin
        // calculator — the user opts into that manually if needed.
        const mappedItems = (extracted.items && extracted.items.length > 0)
          ? extracted.items.map((it) => {
              const listPrice = parseFloat(it.unit_price) || 0;
              const origQty = parseFloat(it.quantity) || 1;
              const discPct = parseFloat(it.discount_pct) || 0;
              const hasPdfDiscount = discPct > 0;
              // Nett line total = list × qty × (1 - disc%). Matches PDF "NETT TOTAL (GBP)".
              const nettLineTotal = hasPdfDiscount
                ? Math.round(listPrice * origQty * (1 - discPct / 100) * 100) / 100
                : null;
              return {
                code: it.code || '',
                description: it.description || '',
                // Collapse qty → 1 for PDF-with-discount rows so Subtotal = sum of line
                // totals exactly matches the PDF. For plain rows (no discount column),
                // keep original qty and per-unit price behaviour.
                quantity: hasPdfDiscount ? 1 : origQty,
                qty_unit: hasPdfDiscount ? 'Each' : (it.qty_unit || 'Each'),
                // For discount-collapsed rows, unit_price is already the FINAL £ total
                // for qty=1 — force unit to 'Each' too so no Cost Factor ever gets
                // applied a second time on top of an already-final total.
                unit: hasPdfDiscount ? 'Each' : (it.unit || 'Each'),
                unit_price: hasPdfDiscount ? nettLineTotal : listPrice,
                cost_factor: it.cost_factor != null ? it.cost_factor : null,
                // This line's own parsed Nett Total (or, for discount rows, the
                // already-final nettLineTotal) — the source of truth for the line
                // total whenever cost_factor is unknown. Without a cost factor
                // there's no reliable way to convert a Pricing-Unit price (e.g.
                // £/M2) into a total from quantity alone, so getLineTotal() falls
                // back to this exact parsed figure instead of silently computing
                // price × quantity as if it were an Each-priced line (see
                // getLineTotal below). Cleared the moment the user edits price or
                // quantity themselves.
                pdf_line_total: hasPdfDiscount ? nettLineTotal : (parseFloat(it.pdf_line_total) > 0 ? parseFloat(it.pdf_line_total) : null),
                // M4 Sales Order Acknowledgements carry a FINAL price for this line —
                // even when the code is later corrected/edited, or the item is added
                // to CRM, this price must never be silently overwritten by a Settings >
                // Pricing sheet lookup. See applyPricingLookup below.
                // Only Encon's own M4 quotes/Sales Order Acknowledgements carry a FINAL price that must
                // never be silently overwritten by a Settings > Pricing sheet lookup (see applyPricingLookup
                // below). A line parsed from any other document (a customer PO, a supplier's own quote) stays
                // editable so the extracted price can be adjusted or margined normally.
                price_locked: !!extracted.is_encon_quote,
                // Display-only metadata for PDF-sourced discounted rows.
                pdf_list_price: hasPdfDiscount ? listPrice : null,
                pdf_discount_pct: hasPdfDiscount ? discPct : null,
                pdf_original_qty: hasPdfDiscount ? origQty : null,
                pdf_original_unit: hasPdfDiscount ? (it.unit || 'Each') : null,
                // Non-Encon documents: the extracted price is a cost/reference price, not a pre-agreed final sale
                // price — feed it straight into this line's own margin calculator (the "List Price" box under the
                // % icon next to Unit Price) so a margin can be added immediately, the same as typing a price in
                // by hand and opening the calculator.
                ...(extracted.is_encon_quote ? {} : {
                  margin_config: { list_price: listPrice, uom_multiplier: 1, discount_pct: 0, rebate_pct: 0, mf_support_value: 0, adhoc_adjustment: 0, margin_pct: 0 },
                }),
              };
            })
          : [{ code: '', description: '', quantity: 1, qty_unit: 'Each', unit: 'Each', unit_price: 0, cost_factor: null, pdf_line_total: null }];

        // Populate form with extracted data
        setQuoteForm(prev => ({
          ...prev,
          customer_name: extracted.customer_name || prev.customer_name,
          customer_address: extracted.customer_address || prev.customer_address,
          quote_number: extracted.quote_number || `Q-${Date.now()}`,
          date: extracted.date || prev.date,
          valid_until: extracted.valid_until || prev.valid_until,
          items: mappedItems,
          notes: extracted.notes || prev.notes,
        }));
        
        // Switch to create mode to show the form
        setCurrentMode('create');
        // Keep the original PDF file around so we can attach it to a Zoho Quote later
        setUploadedPdfFile(file);
        setSelectedFile(null);
        toast.success('PDF data extracted! Review and edit the details below.');

        // Backfill cost factors for any parsed item that didn't already get one
        // from the PDF's own Stock/Pricing unit price columns (falls back to the
        // Cost Factor Master lookup by product code).
        mappedItems.forEach((it, idx) => {
          if (it.code && (it.cost_factor == null)) {
            lookupCostFactorForCode(idx, it.code);
          }
        });
      } else {
        toast.error(response.data.error || 'Failed to extract data from PDF');
      }
    } catch (error) {
      console.error('Error processing upload:', error);
      toast.error('Failed to process uploaded file');
    } finally {
      setProcessing(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file && file.type === 'application/pdf') {
      setSelectedFile(file);
    } else {
      toast.error('Please upload a PDF file');
    }
  };


  // Auto-save timer - saves quote every 30 seconds when in create mode
  useEffect(() => {
    if (!isOpen || currentMode !== 'create') return;
    
    const autoSaveTimer = setInterval(async () => {
      // Only auto-save if there's meaningful data
      if (quoteForm.customer_name || quoteForm.items.some(i => i.description)) {
        try {
          const res = await api.post(`${API}/api/quotes/autosave`, quoteForm);
          // Capture the DB record ID so AddToCrmModal can call link-quote later
          if (!quoteForm._localItemId && res.data.id) {
            setQuoteForm(prev => ({ ...prev, _localItemId: res.data.id }));
          }
          console.log('✓ Quote auto-saved at', new Date().toLocaleTimeString());
        } catch (error) {
          console.error('Auto-save failed:', error);
        }
      }
    }, 30000); // Every 30 seconds

    return () => clearInterval(autoSaveTimer);
  }, [isOpen, currentMode, quoteForm]);


  if (!isOpen) return null;

  // The Create Quote modal always runs from the sub-header down to the bottom of the screen - the same height as the
  // View Price Lists panel beside it - whether or not that panel is open.
  const quoteFullHeight = currentMode === 'create';

  const hasCustomer = !!(custAccount.id || custAccount.name);
  const togglePanel = (id) => setSidePanel((cur) => (cur === id ? null : id));

  // The red X. Unfinished ('create') work is autosaved and minimized into the Activity Bar's "In Progress" tab, never discarded,
  // and its prices stay OFF the customer's price list. Where it goes next depends on where the user came from: Recent Quotes
  // -> back to that list; Customer Management -> back to Customer Management exactly as it was (the parent simply closes this
  // modal); a brand-new quote -> closes outright.
  const requestClose = async () => {
    if (currentMode === 'create') {
      if (quoteForm.customer_name || quoteForm.items?.some(i => i.description)) {
        try { await api.post(`${API}/api/quotes/autosave`, quoteForm); } catch { /* non-fatal */ }
        window.dispatchEvent(new Event('c30:quotes-changed'));
      }
      triggerCrmPromptOrRun(() => {
        endQuoteActivity(true);
        if (prevMode === 'recent') {
          setPrevMode(null);
          setCurrentMode('recent');
        } else {
          onClose();
        }
      });
    } else {
      endQuoteActivity(false);
      onClose();
    }
  };

  // CM tab: jump to this customer's Customer Management page, wherever the quote was opened from. When Customer Management is
  // already open underneath (the quote came from there) this is the same as closing the quote; otherwise Customer Management
  // opens over the quote and closing it brings the quote straight back.
  const openCustomerManagement = () => {
    if (fromCustomerManagement) { requestClose(); return; }
    setCmTarget({ id: quoteForm.zoho_account_id || '', name: (quoteForm.customer_name || '').trim() });
    setCmOpen(true);
  };

  // Edit on a card in the Quotes panel: this quote is autosaved, then the picked one opens in its place.
  const openQuoteFromPanel = async (picked) => {
    if (quoteForm.customer_name || quoteForm.items?.some(i => i.description)) {
      try { await api.post(`${API}/api/quotes/autosave`, quoteForm); } catch { /* non-fatal */ }
    }
    window.dispatchEvent(new Event('c30:quotes-changed'));
    loadQuoteFromRecent(picked, prevMode === 'recent');
  };

  const railBtnClass = (active, disabled) =>
    `flex flex-col items-center justify-center gap-2 py-4 rounded-l-2xl border-2 border-r-0 font-bold text-xs transition-colors ${
      disabled ? 'opacity-40 cursor-not-allowed text-orange-700 bg-orange-50 border-orange-200'
        : active ? 'text-white border-orange-500' : 'text-orange-700 bg-orange-50 border-orange-200 hover:bg-orange-100'
    }`;

  // Modal styles with orange-to-pink gradient border
  const modalStyle = {
    background: 'white',
    borderRadius: '16px',
    border: '3px solid transparent',
    backgroundImage: 'linear-gradient(white, white), linear-gradient(135deg, #ee7203, #ec4899)',
    backgroundOrigin: 'border-box',
    backgroundClip: 'padding-box, border-box',
    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
  };

  // Show LIST £ / DISC % "(note)" columns only when at least one row carries
  // a value — keeps the editor clean for brand-new quotes and encon source
  // quotes that don't include those notes.
  const _items = quoteForm?.items || [];
  const _hasNote = (v) => v !== null && v !== undefined && String(v).trim() !== '';
  const showListNote = _items.some(it => _hasNote(it?.list_price_note ?? it?.pdf_list_price));
  const showDiscNote = _items.some(it => _hasNote(it?.discount_note ?? it?.pdf_discount_pct));
  // Show the Cost Factor / Qty UOM / Qty(Each) / Price UOM / Line Total columns only
  // once a line actually uses something other than plain "Each" — keeps the table
  // in its classic simple layout for the common (non-coverage) case.
  // The Qty(Each) column is only meaningful when a line's Qty UOM actually needs
  // converting from M2/Length coverage into stock units — hide it otherwise.
  const hasQtyConversion = _items.some(it => (it?.qty_unit || 'Each') !== 'Each');
  // M2-mode grid uses a 40-unit template for finer column-width control. The
  // Description span (>12) exceeds Tailwind's default col-span-* scale, and
  // arbitrary-value col-span classes proved unreliable in this build, so we
  // set `gridColumn` directly via inline style instead.
  const getM2DescGridColumn = () => {
    let span = hasQtyConversion ? 14 : 16;
    if (showListNote) span -= 4;
    if (showDiscNote) span -= 4;
    return `span ${span} / span ${span}`;
  };

  return (
    <div
      className={`fixed left-0 right-0 bottom-0 flex items-center justify-center p-4 ${modalV2OverlayClass(modalUiV2)} ${v3w.className}`}
      {...v3w.attrs}
      style={{
        ...(modalUiV2 ? { top: activityBar.headerHeight, zIndex: 45 } : { top: 0, zIndex: 50 }),
        ...v3w.style,
        // Customer Management (opened from the CM tab) sits over the quote; the quote waits underneath, untouched.
        ...(cmOpen ? { opacity: 0, pointerEvents: 'none' } : {}),
        transition: 'opacity 150ms ease',
      }}
    >
      {/* Shell: [CM / Quotes / Orders / Pricing tabs + View Price Lists tab] [quote modal - keeps its size and slides left] [one panel slides out on the right] */}
      <div
        className="flex items-stretch justify-center w-full min-w-0"
        style={{
          maxHeight: modalUiV2 ? `calc(100vh - ${activityBar.headerHeight}px - 32px)` : '90vh',
          ...(quoteFullHeight ? { height: '100%' } : {}),
          // Opened from Customer Management: the quote eases in as Customer Management steps back, so the two feel like one window.
          ...(fromCustomerManagement ? { animation: 'c30QuoteIn 220ms ease-out' } : {}),
        }}
        data-testid="quote-shell"
      >
        {currentMode === 'create' && (
          <div className="hidden md:flex flex-col flex-shrink-0 pt-20" style={{ width: QUOTE_RAIL_W }} data-testid="quote-tab-rail">
            {/* Customer Management tabs: CM jumps to the customer's page; Quotes / Orders / Pricing slide their panel out on the right */}
            <div className="flex flex-col gap-2">
              <button
                type="button"
                onClick={openCustomerManagement}
                disabled={!hasCustomer}
                className={railBtnClass(false, !hasCustomer)}
                style={{ width: QUOTE_RAIL_W }}
                title={hasCustomer ? 'Customer Management' : 'Choose a customer first'}
                data-testid="quote-tab-cm"
              >
                <UserRound className="w-4 h-4" />
                <span style={{ writingMode: 'vertical-rl', letterSpacing: '0.06em' }}>CM</span>
              </button>
              {QUOTE_CUSTOMER_TABS.map(({ id, label, Icon }) => {
                const active = sidePanel === id;
                return (
                  <button
                    key={id}
                    type="button"
                    onClick={() => togglePanel(id)}
                    disabled={!hasCustomer}
                    className={railBtnClass(active, !hasCustomer)}
                    style={{ width: QUOTE_RAIL_W, ...(active ? { backgroundColor: '#ee7203' } : {}) }}
                    title={hasCustomer ? label : 'Choose a customer first'}
                    data-testid={`quote-tab-${id}`}
                  >
                    <Icon className="w-4 h-4" />
                    <span style={{ writingMode: 'vertical-rl', letterSpacing: '0.06em' }}>{label}</span>
                  </button>
                );
              })}
            </div>
            {/* View Price Lists sits further down, with a gap above it */}
            <div className="mt-10">
              <button
                type="button"
                onClick={() => togglePanel('pricelists')}
                className={railBtnClass(sidePanel === 'pricelists', false)}
                style={{ width: QUOTE_RAIL_W, ...(sidePanel === 'pricelists' ? { backgroundColor: '#ee7203' } : {}) }}
                title="View Price Lists"
                data-testid="quote-tab-pricelists"
              >
                <PoundSterling className="w-4 h-4" />
                <span style={{ writingMode: 'vertical-rl', letterSpacing: '0.06em' }}>View Price Lists</span>
              </button>
            </div>
          </div>
        )}
      <div
        className="overflow-y-auto relative rounded-3xl shadow-2xl border border-gray-200 bg-gradient-to-br from-orange-50/30 via-white to-orange-50/10"
        style={{
          ...modalStyle,
          width: '100%',
          maxWidth: QUOTE_MODAL_MAX_W,
          flex: `0 1 ${QUOTE_MODAL_MAX_W}px`,
          minWidth: 0,
          maxHeight: quoteFullHeight ? '100%' : (modalUiV2 ? `calc(100vh - ${activityBar.headerHeight}px - 32px)` : '90vh'),
          ...(quoteFullHeight ? { height: '100%' } : {}),
        }}
        data-testid="quote-modal"
      >
        {/* Red X Close Button — unfinished ('create' mode) work is minimized
            into the "In Progress" Activity Bar tab, not discarded. If this
            quote was reached via Recent Quotes (amending an existing one),
            closing returns to that list (same modal); a brand-new quote
            (from "Create New Quote") just closes outright — no Recent
            Quotes list behind it. */}
        {/* Sticky holder (no height of its own): keeps the red X fixed in the top-right corner while the modal scrolls */}
        <div className="sticky top-0 z-20 h-0">
        <button 
          data-testid="quote-modal-close-btn"
          onClick={requestClose}
          className="absolute top-4 right-4 z-10 w-10 h-10 bg-red-500 hover:bg-red-600 rounded-full flex items-center justify-center transition-colors shadow-lg"
        >
          <X className="w-6 h-6 text-white" />
        </button>
        </div>

        {currentMode === 'create' ? (
          <>
            {/* Header: icon + title, customer, quote ref, date and valid until - all on one line */}
            <div className="v3-modal-bar sticky top-0 bg-white rounded-t-xl px-5 py-2 border-b z-[5]">
              <div className="flex flex-wrap items-center gap-x-3 gap-y-2 pr-14" data-testid="quote-header-row">
                {prevMode === 'recent' && (
                  <button
                    onClick={() => triggerCrmPromptOrRun(() => {
                      endQuoteActivity(true);
                      setPrevMode(null);
                      setCurrentMode('recent');
                    })}
                    className="text-xs text-gray-500 hover:text-orange-600 flex items-center gap-1 transition-colors flex-shrink-0"
                    data-testid="back-to-recent-quotes-btn"
                  >
                    ← Back
                  </button>
                )}
                <TitleIcon className="w-6 h-6 flex-shrink-0" style={{ color: titleIconColor }} data-testid="quote-title-icon" />
                <h2 className="text-lg font-bold text-white leading-tight flex-shrink-0" data-testid="quote-title">Create Quote</h2>
                <div className="flex-shrink-0" style={{ width: 150 }}>
                  <Label className="block text-[10px] font-bold uppercase tracking-wider leading-none text-gray-600 mb-1">Quote Ref</Label>
                  <Input
                    value={quoteForm.quote_number}
                    onChange={(e) => setQuoteForm(prev => ({ ...prev, quote_number: e.target.value }))}
                    className={`h-9 text-sm ${QUOTE_FIELD_CLS}`}
                    data-testid="quote-ref-input"
                  />
                </div>
                <div className="flex-shrink-0" style={{ width: 150 }}>
                  <Label className="block text-[10px] font-bold uppercase tracking-wider leading-none text-gray-600 mb-1">Date</Label>
                  <Input
                    type="date"
                    value={quoteForm.date}
                    onChange={(e) => setQuoteForm(prev => ({ ...prev, date: e.target.value }))}
                    className={`h-9 text-sm px-2 ${QUOTE_FIELD_CLS}`}
                    data-testid="quote-date-input"
                  />
                </div>
                <div className="flex-shrink-0" style={{ width: 150 }}>
                  <Label className="block text-[10px] font-bold uppercase tracking-wider leading-none text-gray-600 mb-1">Valid Until</Label>
                  <Input
                    type="date"
                    value={quoteForm.valid_until}
                    onChange={(e) => setQuoteForm(prev => ({ ...prev, valid_until: e.target.value }))}
                    className={`h-9 text-sm px-2 ${QUOTE_FIELD_CLS}`}
                    data-testid="quote-valid-until-input"
                  />
                </div>
                <Button
                  size="sm"
                  className="ml-auto rounded-xl h-9 px-4 text-white font-semibold flex-shrink-0 whitespace-nowrap"
                  style={{ backgroundColor: '#ee7203' }}
                  onClick={() => setApplyingBulkMargin(!applyingBulkMargin)}
                  data-testid="apply-margin-all-btn"
                >
                  Apply Margin / Increase to All
                </Button>
              </div>
            </div>

            {/* Content */}
            <div className="p-4 space-y-4">
              {/* One row: the customer name on the left and the customer address on the right, both drawn like the quote's other boxes */}
              <div className="grid grid-cols-1 md:grid-cols-2 items-start gap-3" data-testid="quote-top-line">
                <div className="min-w-0" data-testid="quote-header-customer-box">
                  <Label className="block text-[10px] font-bold uppercase tracking-wider leading-none text-gray-600 mb-1">Customer Name *</Label>
                  <CustomerSearchDropdown
                    fieldClass={`h-14 text-base font-semibold ${QUOTE_FIELD_CLS}`}
                    autoFocus={!quoteForm.customer_name && !initialQuote && !resumeSnapshot}
                    value={quoteForm.customer_name}
                    onChange={(val) => setQuoteForm(prev => ({
                      ...prev, customer_name: val,
                      // Typing after a previous selection means the rep is
                      // changing the customer — drop the stale CRM match so
                      // "Add to CRM" doesn't silently attach the wrong account.
                      zoho_account_id: val === prev.customer_name ? prev.zoho_account_id : null,
                    }))}
                    onSelectCustomer={({ address, account_name, zoho_account_id, customer_code }) => setQuoteForm(prev => ({
                      ...prev, customer_name: account_name, customer_address: address || prev.customer_address,
                      zoho_account_id, customer_account_number: customer_code || prev.customer_account_number,
                    }))}
                    placeholder="Customer search"
                  />
                </div>
                <div className="min-w-0">
                  <Label className="block text-[10px] font-bold uppercase tracking-wider leading-none text-gray-600 mb-1">Customer Address</Label>
                  {(() => {
                    const rawAddress = String(quoteForm.customer_address || '');
                    const addressLines = rawAddress.split('\n').map((l) => l.trim()).filter(Boolean);
                    // Typing (or empty): a normal text box. It grows to show every line while you type.
                    if (editingAddress || addressLines.length === 0) {
                      return (
                        <Textarea
                          value={quoteForm.customer_address}
                          onChange={(e) => setQuoteForm(prev => ({ ...prev, customer_address: e.target.value }))}
                          onBlur={() => setEditingAddress(false)}
                          autoFocus={editingAddress}
                          placeholder="Enter customer address"
                          className={`min-h-0 py-1.5 text-sm ${QUOTE_FIELD_CLS}`}
                          style={{ minHeight: 56 }}
                          rows={Math.max(2, rawAddress.split('\n').length)}
                          data-testid="quote-customer-address"
                        />
                      );
                    }
                    // Otherwise: the whole address, 2 lines per column, moving on to the next column as needed - nothing to scroll.
                    const addressColumns = [];
                    for (let i = 0; i < addressLines.length; i += 2) addressColumns.push(addressLines.slice(i, i + 2));
                    return (
                      <div
                        role="button"
                        tabIndex={0}
                        onClick={() => setEditingAddress(true)}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setEditingAddress(true); } }}
                        title="Click to edit the address"
                        className={`grid grid-flow-col auto-cols-fr items-start content-start gap-x-4 px-3 py-1.5 border cursor-text overflow-hidden ${QUOTE_FIELD_CLS}`}
                        style={{ height: 56 }}
                        data-testid="quote-customer-address-columns"
                      >
                        {addressColumns.map((col, c) => (
                          <div key={c} className="min-w-0" data-testid={`quote-customer-address-col-${c}`}>
                            {col.map((line, l) => (
                              <div key={l} className="truncate text-sm leading-5 text-gray-900" title={line}>{line}</div>
                            ))}
                          </div>
                        ))}
                      </div>
                    );
                  })()}
                </div>
              </div>

              {/* Items Section */}
              <div>
                <Label className="block text-sm font-semibold mb-2">Line Items</Label>
                {/* Bulk Margin/Increase Panel */}
                {applyingBulkMargin && (
                  <div className="mb-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                    <h3 className="font-semibold mb-2">Apply Margin / Increase to All Items</h3>
                    
                    {/* Mode Selector */}
                    <div className="flex gap-2 mb-3">
                      <Button
                        size="sm"
                        variant={bulkIncreaseMode === 'margin' ? 'default' : 'outline'}
                        onClick={() => setBulkIncreaseMode('margin')}
                      >
                        Margin Settings
                      </Button>
                      <Button
                        size="sm"
                        variant={bulkIncreaseMode === 'amount' ? 'default' : 'outline'}
                        onClick={() => setBulkIncreaseMode('amount')}
                      >
                        Price Increase (£)
                      </Button>
                      <Button
                        size="sm"
                        variant={bulkIncreaseMode === 'percent' ? 'default' : 'outline'}
                        onClick={() => setBulkIncreaseMode('percent')}
                      >
                        Price Increase (%)
                      </Button>
                    </div>

                    {/* Margin Mode */}
                    {bulkIncreaseMode === 'margin' && (
                      <>
                        <p className="text-xs text-gray-600 mb-3">Apply margin to all items that have a list price set. Leave Discount/Rebate blank to keep each item's own value (e.g. auto-populated from Settings &gt; Pricing).</p>
                        <div className="grid grid-cols-3 gap-3">
                          <div>
                            <Label className="text-sm">Discount (%)</Label>
                            <Input
                              type="number"
                              step="0.1"
                              value={bulkMarginConfig.discount_pct ?? ''}
                              onChange={(e) => {
                                const v = e.target.value;
                                setBulkMarginConfig(prev => ({ ...prev, discount_pct: v === '' ? null : (parseFloat(v) || 0) }));
                              }}
                              placeholder="Keep existing"
                              className="mt-1"
                              data-testid="bulk-margin-discount-input"
                            />
                          </div>
                          <div>
                            <Label className="text-sm">Rebate (%)</Label>
                            <Input
                              type="number"
                              step="0.1"
                              value={bulkMarginConfig.rebate_pct ?? ''}
                              onChange={(e) => {
                                const v = e.target.value;
                                setBulkMarginConfig(prev => ({ ...prev, rebate_pct: v === '' ? null : (parseFloat(v) || 0) }));
                              }}
                              placeholder="Keep existing"
                              className="mt-1"
                              data-testid="bulk-margin-rebate-input"
                            />
                          </div>
                          <div>
                            <Label className="text-sm">Margin (%)</Label>
                            <Input
                              type="number"
                              step="0.1"
                              value={bulkMarginConfig.margin_pct}
                              onChange={(e) => setBulkMarginConfig(prev => ({ ...prev, margin_pct: parseFloat(e.target.value) || 0 }))}
                              className="mt-1"
                              data-testid="bulk-margin-pct-input"
                            />
                          </div>
                        </div>
                      </>
                    )}

                    {/* Amount Increase Mode */}
                    {bulkIncreaseMode === 'amount' && (
                      <>
                        <p className="text-xs text-gray-600 mb-3">Add a fixed amount (£) to all unit prices</p>
                        <div className="max-w-xs">
                          <Label className="text-sm">Increase Amount (£)</Label>
                          <Input
                            type="number"
                            step="0.01"
                            value={bulkIncreaseValue}
                            onChange={(e) => setBulkIncreaseValue(parseFloat(e.target.value) || 0)}
                            className="mt-1"
                            placeholder="e.g. 5.00"
                          />
                        </div>
                      </>
                    )}

                    {/* Percent Increase Mode */}
                    {bulkIncreaseMode === 'percent' && (
                      <>
                        <p className="text-xs text-gray-600 mb-3">Increase all unit prices by a percentage</p>
                        <div className="max-w-xs">
                          <Label className="text-sm">Increase Percentage (%)</Label>
                          <Input
                            type="number"
                            step="0.1"
                            value={bulkIncreaseValue}
                            onChange={(e) => setBulkIncreaseValue(parseFloat(e.target.value) || 0)}
                            className="mt-1"
                            placeholder="e.g. 10"
                          />
                        </div>
                      </>
                    )}

                    <div className="flex gap-2 mt-3">
                      <Button onClick={applyBulkMargin} style={{ backgroundColor: '#ee7203' }} className="text-white">
                        Apply to All Items
                      </Button>
                      <Button variant="outline" onClick={() => setApplyingBulkMargin(false)}>
                        Cancel
                      </Button>
                    </div>
                  </div>
                )}
                
                <div className="rounded-xl border-2 border-gray-200 overflow-hidden" data-quote-lines="true">
                  {/* Header */}
                  <div className="grid grid-cols-[repeat(40,minmax(0,1fr))] gap-2 p-3 text-xs font-semibold text-white" style={{ backgroundColor: '#ee7203' }}>
                    <div className="col-span-6">CODE</div>
                    <div style={{ gridColumn: getM2DescGridColumn() }}>DESCRIPTION</div>
                    <div className="col-span-3 text-center">QTY</div>
                    <div className="col-span-2 text-center" title="Unit the quantity is in">UOM</div>
                    {hasQtyConversion && <div className="col-span-2 text-center" title="Number of stock units, derived from Qty ÷ Cost Factor when Qty UOM is M2/Length">QTY (EA)</div>}
                    <div className="col-span-1 text-center opacity-70">|</div>
                    <div className="col-span-4 text-center">
                      {_items.some(it => (it.unit || 'Each') === 'M2') ? 'M2 PRICE' : _items.some(it => (it.unit || 'Each') === 'M') ? 'M PRICE' : 'UNIT PRICE'}
                    </div>
                    <div className="col-span-2 text-center" title="Unit the price is per">UOM</div>
                    {showListNote && <div className="col-span-4 text-center">LIST £ <span className="font-normal opacity-80">(note)</span></div>}
                    {showDiscNote && <div className="col-span-4 text-center">DISC % <span className="font-normal opacity-80">(note)</span></div>}
                    <div className="col-span-3 text-center">TOTAL</div>
                    <div className="col-span-3"></div>
                  </div>
                  
                  {/* Items */}
                  <div className="divide-y">
                    {quoteForm.items.map((item, index) => {
                      const itemPriceUnit = item.unit || 'Each';
                      return (
                      <div key={index}>
                        <div
                          className={`grid grid-cols-[repeat(40,minmax(0,1fr))] gap-2 p-3 items-start hover:bg-gray-50 cursor-pointer ${sidePanel === 'line' && activeLine === index ? 'bg-orange-50 ring-2 ring-inset ring-orange-400' : 'bg-white'}`}
                          onClick={(e) => { if (e.target.closest && e.target.closest('input, textarea, select, button, a, [role="combobox"], [role="option"], [data-radix-popper-content-wrapper]')) return; openLineEditor(index); }}
                          data-testid={`quote-line-row-${index}`}
                        >
                          <div className="col-span-6 flex gap-1">
                            {/* Pricing Components Toggle - Left of code box */}
                            <button
                              onClick={() => setExpandedMargin(expandedMargin === index ? null : index)}
                              className="w-6 h-6 mt-1.5 rounded flex items-center justify-center flex-shrink-0"
                              style={{ backgroundColor: '#ee7203' }}
                              title="Toggle pricing components"
                            >
                              <ChevronDown className={`w-4 h-4 text-white transition-transform ${expandedMargin === index ? 'rotate-180' : ''}`} />
                            </button>
                            <div className="flex-1 min-w-0">
                              <ProductSearchDropdown
                                value={item.code}
                                onChange={(val) => updateItem(index, 'code', val)}
                                onSelect={(product) => handleProductSelect(index, product)}
                                onBlur={(val) => lookupCostFactorForCode(index, val)}
                                placeholder="Code..."
                                mode="code"
                                data-testid={`item-code-${index}`}
                              />
                            </div>
                          </div>
                          <div style={{ gridColumn: getM2DescGridColumn() }}>
                            <ProductSearchDropdown
                              value={item.description}
                              onChange={(val) => updateItem(index, 'description', val)}
                              onSelect={(product) => handleProductSelect(index, product)}
                              placeholder="Description..."
                            />
                          </div>
                          <div className="col-span-3">
                            <Input
                              type="number"
                              min="1"
                              value={item.quantity}
                              onChange={(e) => updateItem(index, 'quantity', parseInt(e.target.value) || 1)}
                              className={`text-center text-sm h-9 px-1 ${QUOTE_FIELD_CLS}`}
                              data-testid={`item-qty-${index}`}
                            />
                          </div>
                          <div className="col-span-2">
                            <Select value={item.qty_unit || 'Each'} onValueChange={(val) => updateItem(index, 'qty_unit', val)}>
                              <SelectTrigger className={`text-xs h-9 px-1 justify-center [&>svg]:hidden ${QUOTE_SELECT_CLS}`} data-testid={`item-qty-uom-${index}`}>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent className="bg-white">
                                {UOM_OPTIONS.map(u => <SelectItem key={u} value={u}>{uomShort(u)}</SelectItem>)}
                              </SelectContent>
                            </Select>
                          </div>
                          {hasQtyConversion && (
                            <div className="col-span-2 h-9 flex items-center justify-center text-center text-sm font-semibold text-gray-700" data-testid={`item-qty-each-${index}`}>
                              {getQtyEach(item)}
                            </div>
                          )}
                          <div className="col-span-1 h-9 flex items-center justify-center text-gray-300 select-none" aria-hidden="true">|</div>
                          <div className="col-span-4">
                            <div className={`flex items-center ${priceListOpen ? 'gap-0.5' : 'gap-1'}`}>
                              <Input
                                type="number"
                                step="0.01"
                                min="0"
                                value={item.unit_price}
                                onChange={(e) => {
                                  const val = parseFloat(e.target.value) || 0;
                                  updateItem(index, 'unit_price', Math.round(val * 100) / 100);
                                }}
                                onBlur={(e) => {
                                  const val = parseFloat(e.target.value) || 0;
                                  updateItem(index, 'unit_price', Math.round(val * 100) / 100);
                                }}
                                className={`text-center text-sm h-9 min-w-0 px-1 ${QUOTE_FIELD_CLS} ${
                                  getMarginState(item) === 'red' ? 'border-red-500 bg-red-50 focus-visible:ring-red-400' :
                                  getMarginState(item) === 'yellow' ? 'border-yellow-400 bg-yellow-50 focus-visible:ring-yellow-400' : ''
                                }`}
                                placeholder={itemPriceUnit === 'M2' ? '£/m²' : itemPriceUnit === 'M' ? '£/m' : '£0.00'}
                                data-testid={`item-unit-price-${index}`}
                              />
                              {(getMarginState(item) === 'yellow' || quickMarginFor === index) && (
                                <Popover open={quickMarginFor === index} onOpenChange={(v) => setQuickMarginFor(v ? index : null)}>
                                  <PopoverTrigger asChild>
                                    <button
                                      type="button"
                                      className={`shrink-0 flex items-center justify-center rounded-full bg-yellow-400 text-white hover:bg-yellow-500 transition-colors ${priceListOpen ? 'w-4 h-4' : 'w-6 h-6'}`}
                                      title="No margin added — click to add one"
                                      data-testid={`item-add-margin-btn-${index}`}
                                    >
                                      <Plus className={priceListOpen ? 'w-2.5 h-2.5' : 'w-3.5 h-3.5'} />
                                    </button>
                                  </PopoverTrigger>
                                  <PopoverContent className="bg-white w-48 p-3" data-testid={`item-quick-margin-popover-${index}`}>
                                    <Label className="text-xs text-gray-600 mb-1 block">Margin (%)</Label>
                                    <Input
                                      type="number"
                                      step="0.1"
                                      autoFocus
                                      value={item.margin_config?.margin_pct || ''}
                                      onChange={(e) => updateMarginConfig(index, 'margin_pct', parseFloat(e.target.value) || 0)}
                                      placeholder="e.g. 25"
                                      className="h-8 text-sm"
                                      data-testid={`item-quick-margin-input-${index}`}
                                    />
                                  </PopoverContent>
                                </Popover>
                              )}
                            </div>
                            {getMarginState(item) === 'red' && (
                              <p className="text-[10px] text-red-600 text-center mt-0.5 font-medium" data-testid={`item-low-margin-warning-${index}`}>
                                Low margin
                              </p>
                            )}
                            {item.customer_price_date && (
                              <p
                                className="text-[10px] text-gray-400 text-center mt-0.5 leading-tight"
                                title="The last time this customer had this price"
                                data-testid={`item-customer-price-date-${index}`}
                              >
                                {item.customer_price_source === 'order'
                                  ? fmtLastBought(item.customer_price_value ?? item.unit_price, item.customer_price_date)
                                  : `${item.customer_price_source === 'manual' ? 'Price set' : 'Last priced'} ${fmtPriceDate(item.customer_price_date)}`}
                              </p>
                            )}
                          </div>
                          <div className="col-span-2">
                            <Select value={item.unit || 'Each'} onValueChange={(val) => handlePriceUomChange(index, val)}>
                              <SelectTrigger className={`text-xs h-9 px-1 justify-center [&>svg]:hidden ${QUOTE_SELECT_CLS}`} data-testid={`item-price-uom-${index}`}>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent className="bg-white">
                                {UOM_OPTIONS.map(u => <SelectItem key={u} value={u}>{uomShort(u)}</SelectItem>)}
                              </SelectContent>
                            </Select>
                          </div>
                          {/* LIST £ (editable note — styled as plain text, click to edit) */}
                          {showListNote && (
                          <div className="col-span-4">
                            <Input
                              type="number"
                              step="0.01"
                              value={item.pdf_list_price ?? ''}
                              onChange={(e) => {
                                const raw = e.target.value;
                                const val = raw === '' ? null : (parseFloat(raw) || 0);
                                updateItem(index, 'pdf_list_price', val);
                              }}
                              className="text-center text-sm h-9 border-0 bg-transparent shadow-none px-1 text-gray-500 italic hover:bg-gray-50 focus:bg-white focus:ring-1 focus:ring-gray-300 focus:not-italic focus:text-gray-900"
                              placeholder="—"
                              title="Note only — does not affect unit price"
                              data-testid={`item-list-note-${index}`}
                            />
                          </div>
                          )}
                          {/* DISC % (editable note — styled as plain text, click to edit) */}
                          {showDiscNote && (
                          <div className="col-span-4">
                            <Input
                              type="number"
                              step="0.01"
                              value={item.pdf_discount_pct ?? ''}
                              onChange={(e) => {
                                const raw = e.target.value;
                                const val = raw === '' ? null : (parseFloat(raw) || 0);
                                updateItem(index, 'pdf_discount_pct', val);
                              }}
                              className="text-center text-sm h-9 border-0 bg-transparent shadow-none px-1 text-gray-500 italic hover:bg-gray-50 focus:bg-white focus:ring-1 focus:ring-gray-300 focus:not-italic focus:text-gray-900"
                              placeholder="—"
                              title="Note only — does not affect unit price"
                              data-testid={`item-disc-note-${index}`}
                            />
                          </div>
                          )}
                          <div className="col-span-3 h-9 flex items-center justify-center text-center text-sm font-bold" style={{ color: '#ee7203' }} data-testid={`item-line-total-${index}`}>
                            £{formatCurrency(getLineTotal(item))}
                          </div>
                          <div className="col-span-3 h-9 flex items-center justify-center gap-0.5 text-center">
                            <button
                              type="button"
                              onClick={() => openLineEditor(index)}
                              className="p-1 text-orange-600 hover:bg-orange-50 rounded"
                              title="Open this line in the workspace"
                              data-testid={`item-open-editor-${index}`}
                            >
                              <Edit2 className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => removeItem(index)}
                              className="p-1 text-red-500 hover:bg-red-50 rounded"
                              disabled={quoteForm.items.length === 1}
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                        
                        {/* Margin Calculator Panel - Compact - Now 6 boxes all same width */}
                        {expandedMargin === index && (
                          <div className="p-2 bg-gray-50 border-t border-gray-200">
                            <div className="grid grid-cols-9 gap-2">
                              <div>
                                <div className="flex items-center justify-between h-4">
                                  <Label className="text-[10px] text-gray-600">List Price (£)</Label>
                                  {item.pricing_updated_label && (
                                    <span className="text-[9px] text-gray-400" title="Effective date of this List Price in Settings > Pricing" data-testid={`item-pricing-updated-${index}`}>
                                      Update: {item.pricing_updated_label}
                                    </span>
                                  )}
                                </div>
                                <Input
                                  type="number"
                                  step="0.01"
                                  value={item.margin_config?.list_price || 0}
                                  onChange={(e) => updateMarginConfig(index, 'list_price', parseFloat(e.target.value) || 0)}
                                  className={`text-xs h-7 mt-0.5 ${calcBoxClass(item.margin_config?.list_price)}`}
                                  data-testid={`item-list-price-${index}`}
                                />
                              </div>
                              <div>
                                <Label className="text-[10px] text-gray-600 h-4 block truncate" title="M2 covered per stock unit (auto-filled from the Cost Factor Master, editable)">Cost Factor (m²/unit)</Label>
                                <Input
                                  type="number"
                                  step="0.001"
                                  min="0"
                                  value={item.cost_factor ?? ''}
                                  onChange={(e) => {
                                    const raw = e.target.value;
                                    updateItem(index, 'cost_factor', raw === '' ? null : (parseFloat(raw) || 0));
                                  }}
                                  className={`text-xs h-7 mt-0.5 ${hasValidCostFactor(item) ? 'border-green-400 bg-green-50' : (itemPriceUnit !== 'Each' ? 'border-red-500 bg-red-50' : '')}`}
                                  placeholder={itemPriceUnit !== 'Each' ? 'N/A' : '1'}
                                  title={itemPriceUnit !== 'Each' && !hasValidCostFactor(item)
                                    ? 'No Cost Factor on record for this product yet: the Total is kept exactly as parsed from the source document until one is added'
                                    : 'Cost Factor: M2 covered per stock unit (auto-filled from Cost Factor Master, editable)'}
                                  data-testid={`item-cf-${index}`}
                                />
                              </div>
                              <div>
                                <div className="flex items-center justify-between gap-1 h-4">
                                  <Label className="text-[10px] text-gray-600 whitespace-nowrap">{priceListOpen ? 'CF Multiplier' : 'Cost Factor Multiplier'}</Label>
                                  <button
                                    type="button"
                                    onClick={() => updateMarginConfig(index, 'uom_multiplier', getItemCostFactor(item))}
                                    className="text-[9px] font-semibold px-1.5 py-0.5 rounded leading-none bg-orange-100 text-orange-700 hover:bg-orange-200 whitespace-nowrap flex-shrink-0"
                                    title={`Use this product's Cost Factor (${getItemCostFactor(item)})`}
                                  >
                                    Use CF
                                  </button>
                                </div>
                                <Input
                                  type="number"
                                  step="any"
                                  min="0"
                                  value={item.margin_config?.uom_multiplier ?? 1}
                                  onChange={(e) => {
                                    const num = parseFloat(e.target.value);
                                    if (!isNaN(num)) updateMarginConfig(index, 'uom_multiplier', num);
                                  }}
                                  onBlur={(e) => {
                                    const num = parseFloat(e.target.value);
                                    if (isNaN(num) || e.target.value === '') updateMarginConfig(index, 'uom_multiplier', 1);
                                  }}
                                  className={`text-xs h-7 mt-0.5 ${calcBoxClass(item.margin_config?.uom_multiplier ?? 1)}`}
                                  title="Cost Factor multiplier — applied to List Price. Auto-fills from the product's Cost Factor when Price UOM is M2/M; defaults to 1 for Each (click 'Use CF' or type your own)."
                                  data-testid={`item-cost-factor-${index}`}
                                />
                              </div>
                              <div>
                                <Label className="text-[10px] text-gray-600 h-4 block">Discount (%)</Label>
                                <Input
                                  type="number"
                                  step="0.1"
                                  value={item.margin_config?.discount_pct || 0}
                                  onChange={(e) => updateMarginConfig(index, 'discount_pct', parseFloat(e.target.value) || 0)}
                                  className={`text-xs h-7 mt-0.5 ${calcBoxClass(item.margin_config?.discount_pct)}`}
                                  data-testid={`item-discount-${index}`}
                                />
                              </div>
                              <div>
                                <Label
                                  className="text-[10px] text-gray-600 h-4 block truncate"
                                  title={item.ho_support_matched
                                    ? `Matched ${item.ho_support_supplier_code} / ${item.ho_support_group} in the H/O Support Matrix — click Apply to claim it back as a cost of sale (pre-Rebate)`
                                    : 'No H/O Support Matrix match for this code — type a % manually if one applies'}
                                >
                                  H/O Support (%)
                                </Label>
                                <div className="flex gap-1">
                                  <Input
                                    type="number"
                                    step="0.1"
                                    value={item.margin_config?.ho_support_pct ?? ''}
                                    onChange={(e) => updateMarginConfig(index, 'ho_support_pct', e.target.value === '' ? null : (parseFloat(e.target.value) || 0))}
                                    disabled={item.ho_support_matched && !item.margin_config?.ho_support_applied}
                                    placeholder={item.ho_support_matched ? '' : '—'}
                                    className={`text-xs h-7 mt-0.5 ${
                                      item.margin_config?.ho_support_applied
                                        ? calcBoxClass(item.margin_config?.ho_support_pct)
                                        : 'bg-gray-100 text-gray-400 border-gray-200'
                                    }`}
                                    data-testid={`item-ho-support-pct-${index}`}
                                  />
                                  <button
                                    type="button"
                                    onClick={() => toggleHoSupportApply(index)}
                                    className={`text-[9px] font-semibold px-1.5 h-7 mt-0.5 rounded leading-none shrink-0 ${
                                      item.margin_config?.ho_support_applied
                                        ? 'bg-green-600 text-white'
                                        : 'bg-orange-100 text-orange-700 hover:bg-orange-200'
                                    }`}
                                    title={item.margin_config?.ho_support_applied ? 'Click to undo — restores Ad-hoc' : 'Apply this H/O Support % into Ad-hoc as a minus'}
                                    data-testid={`item-ho-support-apply-${index}`}
                                  >
                                    {item.margin_config?.ho_support_applied ? 'Applied' : 'Apply'}
                                  </button>
                                </div>
                              </div>
                              <div>
                                <Label className="text-[10px] text-gray-600 h-4 block">Rebate (%)</Label>
                                <Input
                                  type="number"
                                  step="0.1"
                                  value={item.margin_config?.rebate_pct || 0}
                                  onChange={(e) => updateMarginConfig(index, 'rebate_pct', parseFloat(e.target.value) || 0)}
                                  className={`text-xs h-7 mt-0.5 ${calcBoxClass(item.margin_config?.rebate_pct)}`}
                                  data-testid={`item-rebate-${index}`}
                                />
                              </div>
                              <div>
                                <Label className="text-[10px] text-gray-600 h-4 block truncate" title="Manufacturer price support — a pence-per-unit amount subtracted after Rebate. Pick a document from the dropdown to auto-fill it for this line's product code, or type your own.">
                                  MF Support (p){item.mf_support_document_name && <span className="text-gray-400"> · {item.mf_support_document_name}</span>}
                                </Label>
                                <div className="flex gap-1">
                                  <Input
                                    type="number"
                                    step="0.1"
                                    value={((item.margin_config?.mf_support_value || 0) * 100) || ''}
                                    onChange={(e) => updateMarginConfig(index, 'mf_support_value', (parseFloat(e.target.value) || 0) / 100)}
                                    className={`text-xs h-7 mt-0.5 ${calcBoxClass(item.margin_config?.mf_support_value)}`}
                                    placeholder="0.0"
                                    data-testid={`item-mf-support-${index}`}
                                  />
                                  <Popover>
                                    <PopoverTrigger asChild>
                                      <button
                                        type="button"
                                        className="h-7 w-6 mt-0.5 flex items-center justify-center border rounded hover:bg-gray-100 shrink-0"
                                        title="Pick a support document for this line"
                                        data-testid={`item-mf-support-doc-btn-${index}`}
                                      >
                                        <ChevronDown className="w-3 h-3 text-gray-500" />
                                      </button>
                                    </PopoverTrigger>
                                    <PopoverContent className="w-56 p-1 bg-white" align="end">
                                      {mfSupportDocs.length === 0 ? (
                                        <p className="text-xs text-gray-400 p-2">No support documents yet — add one in Settings &gt; Pricing &gt; Manufacturer Price Support.</p>
                                      ) : mfSupportDocs.map((doc) => (
                                        <button
                                          key={doc.id}
                                          type="button"
                                          onClick={() => applyMfSupportToLine(index, doc)}
                                          className="w-full text-left text-xs px-2 py-1.5 hover:bg-orange-50 rounded"
                                          data-testid={`item-mf-support-doc-option-${index}-${doc.id}`}
                                        >
                                          <span className="font-medium">{doc.supplier_name}</span> — {doc.name}
                                        </button>
                                      ))}
                                    </PopoverContent>
                                  </Popover>
                                </div>
                              </div>
                              <div>
                                {/* Label and Add/Subtract buttons on same line - Fixed height container */}
                                <div className="flex items-center justify-between h-4">
                                  <Label className="text-[10px] text-gray-600">Ad-hoc (£)</Label>
                                  <div className="flex gap-0.5">
                                    <button
                                      type="button"
                                      onClick={() => {
                                        const currentValue = Math.abs(item.margin_config?.adhoc_adjustment || 0);
                                        updateMarginConfig(index, 'adhoc_adjustment', currentValue);
                                      }}
                                      className={`text-[9px] font-semibold px-1.5 py-0.5 rounded leading-none ${
                                        (item.margin_config?.adhoc_adjustment || 0) >= 0 
                                          ? 'bg-green-500 text-white' 
                                          : 'bg-gray-200 text-gray-500'
                                      }`}
                                      title="Add"
                                    >
                                      +
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => {
                                        const currentValue = Math.abs(item.margin_config?.adhoc_adjustment || 0);
                                        updateMarginConfig(index, 'adhoc_adjustment', -currentValue);
                                      }}
                                      className={`text-[9px] font-semibold px-1.5 py-0.5 rounded leading-none ${
                                        (item.margin_config?.adhoc_adjustment || 0) < 0 
                                          ? 'bg-red-500 text-white' 
                                          : 'bg-gray-200 text-gray-500'
                                      }`}
                                      title="Subtract"
                                    >
                                      -
                                    </button>
                                  </div>
                                </div>
                                {/* Input box aligned with other inputs */}
                                <Input
                                  type="number"
                                  step="0.01"
                                  value={Math.abs(item.margin_config?.adhoc_adjustment || 0)}
                                  onChange={(e) => {
                                    const value = parseFloat(e.target.value) || 0;
                                    const isNegative = (item.margin_config?.adhoc_adjustment || 0) < 0;
                                    updateMarginConfig(index, 'adhoc_adjustment', isNegative ? -value : value);
                                  }}
                                  className={`text-xs h-7 mt-0.5 ${calcBoxClass(item.margin_config?.adhoc_adjustment)}`}
                                  placeholder="0.00"
                                  data-testid={`item-adhoc-${index}`}
                                />
                              </div>
                              <div>
                                <Label className="text-[10px] text-gray-600 h-4 block">Margin (%)</Label>
                                <Input
                                  type="number"
                                  step="0.1"
                                  value={item.margin_config?.margin_pct || 0}
                                  onChange={(e) => updateMarginConfig(index, 'margin_pct', parseFloat(e.target.value) || 0)}
                                  className={`text-xs h-7 mt-0.5 ${calcBoxClass(item.margin_config?.margin_pct, { isMargin: true })}`}
                                  data-testid={`item-margin-pct-${index}`}
                                />
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                      );
                    })}
                  </div>
                  
                  {/* Add line: the old always-ready blank line is now just this button (the + key does the same from anywhere in the lines) */}
                  <button
                    type="button"
                    onClick={addNewLineAndEdit}
                    className="w-full flex items-center justify-center gap-2 py-2.5 text-sm font-semibold border-t-2 border-dashed border-orange-300 text-orange-600 hover:bg-orange-50 transition-colors"
                    data-testid="quote-add-line-btn"
                    title="Add a new line (press + on the keyboard)"
                  >
                    <Plus className="w-4 h-4" /> Add line <span className="text-[11px] font-normal opacity-70">( + key )</span>
                  </button>

                  {/* Totals */}
                  <div className="bg-gray-100 p-4 space-y-2">
                    <div className="flex justify-end gap-8 text-sm">
                      <span className="text-gray-600">Subtotal:</span>
                      <span className="font-semibold w-24 text-right">£{formatCurrency(calculateSubtotal())}</span>
                    </div>
                    <div className="flex justify-end gap-8 text-sm">
                      <span className="text-gray-600">VAT (20%):</span>
                      <span className="font-semibold w-24 text-right">£{formatCurrency(calculateVAT())}</span>
                    </div>
                    <div className="flex justify-end gap-8 text-lg border-t pt-2">
                      <span className="font-bold">GRAND TOTAL:</span>
                      <span className="font-bold w-24 text-right" style={{ color: '#ee7203' }}>£{formatCurrency(calculateTotal())}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Notes */}
              <div>
                <Label className="text-sm font-semibold">Additional Notes</Label>
                <Textarea
                  value={quoteForm.notes}
                  onChange={(e) => setQuoteForm(prev => ({ ...prev, notes: e.target.value }))}
                  placeholder="Data sheet available on request..."
                  rows={2}
                  className={`mt-1 ${QUOTE_FIELD_CLS}`}
                />
              </div>

              {/* Action buttons — all on ONE line (Save, PDF, Email, CRM, Copy Quote) so the quote itself gets more room */}
              <div className="flex flex-nowrap items-stretch gap-2" data-testid="quote-action-row">
                <Button
                  onClick={saveQuoteAndClose}
                  disabled={saving || processing}
                  className="flex-1 min-w-0 h-11 px-2 text-sm rounded-xl text-white font-semibold whitespace-nowrap"
                  style={{ backgroundColor: '#9333ea' }}
                  title="Save this quote, add its prices to the customer's price list and close it"
                  data-testid="save-quote-btn"
                >
                  {saving ? 'Saving…' : (
                    <>
                      <Save className="w-4 h-4 mr-1.5 flex-shrink-0" />
                      Save
                    </>
                  )}
                </Button>
                <Button
                  onClick={generateAndDownloadPDF}
                  disabled={processing}
                  className="flex-[1.5] min-w-0 h-11 px-2 text-sm rounded-xl text-white font-semibold whitespace-nowrap"
                  style={{ backgroundColor: '#ee7203' }}
                  data-testid="download-quote-pdf-btn"
                >
                  {processing ? 'Generating…' : (
                    <>
                      <Download className="w-4 h-4 mr-1.5 flex-shrink-0" />
                      <span className="truncate">{priceListOpen ? 'PDF' : 'Generate & Download PDF'}</span>
                    </>
                  )}
                </Button>
                <Button
                  onClick={sendToMyEmail}
                  disabled={sending}
                  variant="outline"
                  className="flex-1 min-w-0 h-11 px-2 text-sm rounded-xl border-2 font-semibold whitespace-nowrap"
                  style={{ borderColor: '#ee7203', color: '#ee7203' }}
                  data-testid="email-quote-self-btn"
                >
                  {sending ? 'Sending…' : (
                    <>
                      <Mail className="w-4 h-4 mr-1.5 flex-shrink-0" />
                      <span className="truncate">{priceListOpen ? 'Email' : 'Send to My Email'}</span>
                    </>
                  )}
                </Button>
                {quoteForm.zoho_quote_url ? (
                  <Button
                    asChild
                    className="flex-1 min-w-0 h-11 px-2 text-sm rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-semibold whitespace-nowrap"
                    data-testid="view-quote-in-crm-btn"
                  >
                    <a
                      href={quoteForm.zoho_quote_url}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <ExternalLink className="w-4 h-4 mr-1.5 flex-shrink-0" />
                      <span className="truncate">View in CRM</span>
                    </a>
                  </Button>
                ) : (
                  <Button
                    onClick={handleOpenAddToCrm}
                    disabled={processing || !quoteForm.customer_name || !quoteForm.items?.length}
                    className="flex-1 min-w-0 h-11 px-2 text-sm rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-semibold whitespace-nowrap"
                    data-testid="add-quote-to-crm-btn"
                  >
                    <ExternalLink className="w-4 h-4 mr-1.5 flex-shrink-0" />
                    <span className="truncate">Add to CRM</span>
                  </Button>
                )}
                <Button
                  data-testid="copy-quote-btn"
                  onClick={async () => {
                    // 1. Save the current quote first
                    if (quoteForm.customer_name || quoteForm.items?.some(i => i.description)) {
                      try { await api.post(`${API}/api/quotes/autosave`, quoteForm); } catch { /* non-fatal */ }
                    }
                    // 2. Build a clean copy: new quote_number, today's dates, strip identity
                    const today = new Date();
                    const validUntil = new Date(today.getTime() + 21 * 24 * 60 * 60 * 1000);
                    const copied = {
                      customer_name: quoteForm.customer_name,
                      customer_address: quoteForm.customer_address,
                      quote_number: `Q-${Date.now()}`,
                      date: today.toISOString().split('T')[0],
                      valid_until: validUntil.toISOString().split('T')[0],
                      items: JSON.parse(JSON.stringify(quoteForm.items || [])),
                      notes: quoteForm.notes,
                      terms: quoteForm.terms,
                    };
                    setQuoteForm(copied);
                    toast.success('Quote copied — editing as a new quote');
                  }}
                  variant="outline"
                  className="flex-1 min-w-0 h-11 px-2 text-sm rounded-xl border-2 font-semibold whitespace-nowrap hover:bg-orange-50"
                  style={{ borderColor: '#ee7203', color: '#ee7203' }}
                >
                  <Copy className="w-4 h-4 mr-1.5 flex-shrink-0" />
                  <span className="truncate">{priceListOpen ? 'Copy' : 'Copy Quote'}</span>
                </Button>
              </div>
            </div>
          </>
        ) : currentMode === 'recent' ? (
          <>
            {/* Recent Quotes Mode */}
            <div className="p-6">
              <div className="flex items-center gap-3 mb-5">
                <Clock className="w-7 h-7" style={{ color: '#ee7203' }} />
                <h2 className="text-xl font-bold" style={{ color: '#ee7203' }}>Recent Quotes</h2>
                <span className="text-xs text-gray-400 ml-auto">Past 30 days · Editor + PDF + Automation</span>
              </div>
              <RecentQuotesView
                user={user}
                onSelect={(quote) => loadQuoteFromRecent(quote, true)}
              />
            </div>
          </>
        ) : (
          <>
            {/* Upload Mode */}
            <div className="p-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-11 h-11 rounded-xl flex items-center justify-center shadow-sm" style={{ background: 'linear-gradient(135deg, #ee7203 0%, #ff8d2a 100%)' }}>
                  <Upload className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h2 className="text-2xl font-extrabold tracking-tight" style={{ color: '#ee7203' }}>Upload Quote PDF</h2>
                  <p className="text-xs text-gray-500">Parse & map each line into an editable quote</p>
                </div>
              </div>

              <div
                className={`rounded-2xl border-2 border-dashed p-12 text-center transition-all shadow-inner ${
                  isDragging ? 'border-orange-500 bg-orange-50 scale-[1.01]' : 'border-gray-300 bg-gray-50/60'
                }`}
                onDrop={handleDrop}
                onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                onDragLeave={() => setIsDragging(false)}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf"
                  className="hidden"
                  onChange={(e) => setSelectedFile(e.target.files[0])}
                />
                
                {selectedFile ? (
                  <div>
                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-emerald-100 mb-4">
                      <FileText className="w-8 h-8 text-emerald-600" />
                    </div>
                    <p className="font-semibold text-lg mb-4 text-gray-900">{selectedFile.name}</p>
                    <Button
                      onClick={() => handleFileUpload(selectedFile)}
                      disabled={processing}
                      size="lg"
                      className="rounded-xl h-11 px-6 text-white font-semibold shadow-md hover:shadow-lg transition-all"
                      style={{ background: 'linear-gradient(135deg, #ee7203 0%, #ff8d2a 100%)' }}
                    >
                      {processing ? 'Processing...' : 'Process & Download'}
                    </Button>
                  </div>
                ) : (
                  <div>
                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gray-100 mb-4">
                      <Upload className="w-8 h-8 text-gray-400" />
                    </div>
                    <p className="text-gray-600 text-lg mb-4">Drag & drop a PDF here, or</p>
                    <Button variant="outline" size="lg" onClick={() => fileInputRef.current?.click()} className="rounded-xl h-11 px-6">
                      Browse Files
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>

        {/* Price Lists panel - grows out to the RIGHT of the quote modal (B29 price lists for now) */}
        {currentMode === 'create' && (
          <div
            className="relative hidden md:block flex-shrink-0 rounded-3xl border-2 border-orange-300 bg-white shadow-2xl overflow-hidden"
            style={{
              height: priceListOpen ? '100%' : undefined,
              width: priceListOpen ? (sidePanel === 'pricelists' ? QUOTE_PANEL_W : sidePanel === 'line' ? QUOTE_LINE_PANEL_W : QUOTE_CUSTOMER_PANEL_W) : 0,
              minWidth: priceListOpen ? 320 : 0,
              marginLeft: priceListOpen ? QUOTE_SHELL_GAP : 0,
              opacity: priceListOpen ? 1 : 0,
              borderWidth: priceListOpen ? 2 : 0,
              transition: 'width 300ms ease, margin 300ms ease, opacity 200ms ease',
            }}
            data-testid="quote-side-panel"
            aria-hidden={!priceListOpen}
          >
            {/* A visible way to slide any panel back: a left arrow on its left edge (the line workspace has its own in its header) */}
            {priceListOpen && sidePanel !== 'line' && (
              <button
                type="button"
                onClick={() => setSidePanel(null)}
                className="absolute left-0 top-3 z-20 flex h-10 w-5 items-center justify-center rounded-r-xl text-white hover:opacity-90"
                style={{ backgroundColor: '#ee7203' }}
                title="Slide this panel back"
                aria-label="Slide this panel back"
                data-testid="quote-side-panel-back"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
            )}
            {sidePanel === 'line' && (
              <QuoteLineEditor
                index={Math.min(activeLine, Math.max(quoteForm.items.length - 1, 0))}
                item={quoteForm.items[Math.min(activeLine, Math.max(quoteForm.items.length - 1, 0))]}
                lineCount={quoteForm.items.length}
                onClose={closeLineEditor}
                onAddLine={addNewLineAndEdit}
                onGoTo={goToLine}
                focusSignal={lineFocusSignal}
                ctx={{
                  ProductSearch: ProductSearchDropdown, updateItem, updateMarginConfig, handleProductSelect, handlePriceUomChange, lookupCostFactorForCode,
                  openProductGroup: () => setGroupModalOpen(true), globalSupportDocId, applyGlobalSupportDocument,
                  toggleHoSupportApply, applyMfSupportToLine, mfSupportDocs, getMarginState, calcBoxClass, getLineTotal, hasValidCostFactor, getItemCostFactor,
                  getQtyEach, calculateMarginCost, formatCurrency, fmtPriceDate, fmtLastBought, uomShort, UOM_OPTIONS,
                }}
              />
            )}
            {sidePanel === 'pricelists' && <QuotePriceListsPanel onAddB29Product={addFromPriceList} onAddSupplierProduct={addFromSupplierPriceList} />}
            {sidePanel === 'quotes' && hasCustomer && custMods?.Quotes && (
              <custMods.Quotes key={`q-${custAccount.id || custAccount.name}`} account={custAccount} user={user} onOpenQuote={openQuoteFromPanel} />
            )}
            {sidePanel === 'orders' && hasCustomer && custMods?.Orders && (
              <custMods.Orders key={`o-${custAccount.id || custAccount.name}`} account={custAccount} />
            )}
            {sidePanel === 'pricing' && hasCustomer && custMods?.Pricing && (
              <custMods.Pricing key={`p-${custAccount.id || custAccount.name}`} account={custAccount} onAddToQuote={addFromCustomerPriceList} />
            )}
          </div>
        )}
      </div>

      {/* Customer Management opened from the CM tab — sits over the quote; closing it brings the quote straight back */}
      {cmOpen && custMods?.CM && (
        <custMods.CM
          isOpen={true}
          embeddedFromQuote={true}
          initialAccount={cmTarget.id ? { id: cmTarget.id, name: cmTarget.name } : null}
          initialSearch={cmTarget.id ? null : cmTarget.name}
          onClose={() => setCmOpen(false)}
        />
      )}

      {/* Add Product Group window (opens on top of the quote) */}
      <ProductGroupModal isOpen={groupModalOpen} onClose={() => setGroupModalOpen(false)} onAdd={addProductGroup} />

      {/* Add-to-CRM Modal */}
      {showCrmModal && (
        <AddToCrmModal
          isOpen={showCrmModal}
          onClose={() => setShowCrmModal(false)}
          quoteForm={{ ...quoteForm, items: withoutBlankQuoteLines(quoteForm.items) }}
          pdfFile={uploadedPdfFile}
          subtotal={calculateSubtotal()}
          onCrmSuccess={(url) => {
            // Update quoteForm so the "View in CRM" button appears
            setQuoteForm(prev => ({ ...prev, zoho_quote_url: url }));
            // Notify RecentQuotesPicker to refresh so the card turns blue
            window.dispatchEvent(new Event('c30:quotes-changed'));
          }}
        />
      )}

      {/* Revise-in-CRM prompt — shown once when leaving/generating a PDF for an
          already-in-CRM Recent Quote that's been edited this session. */}
      {showCrmRevisePrompt && (
        <CrmRevisePromptModal
          revising={revisingQuote}
          onNo={() => {
            crmPromptResolvedRef.current = true;
            setShowCrmRevisePrompt(false);
            pendingCrmActionRef.current?.();
            pendingCrmActionRef.current = null;
          }}
          onYes={async () => {
            setRevisingQuote(true);
            try {
              const res = await api.post(`${API}/api/zoho/quotes/${quoteForm.zoho_quote_id}/revise`, {
                local_quote_id: quoteForm._localItemId,
                list_price: calculateSubtotal(),
              });
              setQuoteForm(prev => ({ ...prev, zoho_quote_id: res.data.quote_id, zoho_quote_url: res.data.quote_url }));
              window.dispatchEvent(new Event('c30:quotes-changed'));
              toast.success('Quote revised in CRM — original marked Closed Revised');
            } catch (e) {
              toast.error(e?.response?.data?.detail?.message || e?.response?.data?.detail || 'Failed to revise quote in CRM');
            } finally {
              setRevisingQuote(false);
              crmPromptResolvedRef.current = true;
              setShowCrmRevisePrompt(false);
              pendingCrmActionRef.current?.();
              pendingCrmActionRef.current = null;
            }
          }}
        />
      )}
    </div>
  );
};

// Main Quotes Component with Dropdown (legacy, can still be used standalone)
const Quotes = ({ user }) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const [modalMode, setModalMode] = useState(null); // 'create' or 'upload'
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const openModal = (mode) => {
    setModalMode(mode);
    setShowDropdown(false);
  };

  return (
    <>
      {/* Dropdown Trigger - can be placed anywhere, typically in navigation */}
      <div className="relative inline-block" ref={dropdownRef}>
        <Button
          onClick={() => setShowDropdown(!showDropdown)}
          className="gap-2"
          style={{ backgroundColor: '#ee7203' }}
        >
          <FileText className="w-4 h-4" />
          Quotes
          <ChevronDown className={`w-4 h-4 transition-transform ${showDropdown ? 'rotate-180' : ''}`} />
        </Button>

        {showDropdown && (
          <div 
            className="absolute top-full left-0 mt-2 w-48 rounded-lg shadow-xl overflow-hidden z-50"
            style={{
              background: 'rgba(35, 35, 35, 0.98)',
              border: '2px solid rgba(238, 114, 3, 0.5)'
            }}
          >
            <button
              onClick={() => openModal('create')}
              className="w-full px-4 py-3 text-left text-white hover:bg-white/10 flex items-center gap-3 transition-colors"
            >
              <Plus className="w-4 h-4" style={{ color: '#ee7203' }} />
              Create New Quote
            </button>
            <button
              onClick={() => openModal('recent')}
              className="w-full px-4 py-3 text-left text-white hover:bg-white/10 flex items-center gap-3 transition-colors border-t border-white/10"
            >
              <Clock className="w-4 h-4" style={{ color: '#ee7203' }} />
              Recent Quotes
            </button>
            <button
              onClick={() => openModal('upload')}
              className="w-full px-4 py-3 text-left text-white hover:bg-white/10 flex items-center gap-3 transition-colors border-t border-white/10"
            >
              <Upload className="w-4 h-4" style={{ color: '#ee7203' }} />
              Upload PDF
            </button>
          </div>
        )}
      </div>

      {/* Modal */}
      <QuoteModal 
        isOpen={modalMode !== null} 
        onClose={() => setModalMode(null)} 
        mode={modalMode}
        user={user}
      />
    </>
  );
};

// Export the Modal component separately for use in M45NavPills
Quotes.Modal = QuoteModal;
// Reused by Customer Management: the Recent Quotes list (filterable to one customer) and the line-item product search.
Quotes.RecentQuotesView = RecentQuotesView;
Quotes.ProductSearchDropdown = ProductSearchDropdown;

export default Quotes;
